// import * as MPAApplication from '@libs/MPAApplication/index';
import * as STD      from '@emersya/mpa-std-lib';
import * as BasketNS from '@emersya/mpa-basket-lib';
import CM            from '@emersya/mpa-components-manager-lib';
import {
	Loader,
	Text,
	Input,
	InputSelect,
	InputImage,
	InputButton,
	Image,
	ModalsZone
}                    from '@emersya/mpa-components-lib';

declare const ENGINE_VERSION : string; /* Filed by the build. */
declare type TViewpoint =
	'top'
	| 'front'
	| 'frontLeft'
	| 'frontRight'
	| 'left'
	| 'right'
	| 'back'
	| 'backLeft'
	| 'backRight';

export interface IMPAEngineApplicationComponentConfiguration extends ApplicationNS.Configuration {
	readonly modules : EngineModule.IModuleInfos[];
	readonly pdfTranslations : Record<string, Record<string, string>>;
	readonly products : {
		readonly SKU : string;
		readonly 'viewer-code' : string;
		readonly  iconURL : string;
		readonly translations : Record<string, STD.translationsManager.TranslationMapping>;
		readonly pricing : BasketNS.Pricing.TPricingSerialized | null;
		readonly pdfName : string;
	}[];

	readonly theme : {
		foreground : Record<'colorful-light' | 'colorful-medium' | 'colorful-dark' | 'colorless-light' | 'colorless-medium' | 'colorless-dark' | 'success-light' | 'success-medium' | 'success-dark' | 'warning-light' | 'warning-medium' | 'warning-dark' | 'error-light' | 'error-medium' | 'error-dark', string>;
		background : Record<'colorful-light' | 'colorful-medium' | 'colorful-dark' | 'colorless-light' | 'colorless-medium' | 'colorless-dark' | 'success-light' | 'success-medium' | 'success-dark' | 'warning-light' | 'warning-medium' | 'warning-dark' | 'error-light' | 'error-medium' | 'error-dark', string>;
		customCSS : Record<string, string>;
	};

	readonly productsComponents : {
		readonly type : string;
		readonly category : string; /* should be nullable but not the time for now! */
		readonly SKU : string;
		readonly products : string[];
		readonly translations : Record<string, STD.translationsManager.TranslationMapping>;
		readonly iconURL : string | null;
		readonly pricing : BasketNS.Pricing.TPricingSerialized | null;
		readonly viewerProjectCode : string | null;
		readonly viewerCode : string | null;
		readonly viewerComponentSKU : string | null;
		readonly boundingBox : {
			readonly width : number;
			readonly depth : number;
			readonly height : number;
		} | null;
		readonly fixedPositionsSet : string | null;
		// readonly metadataTypeSpecific : Record<string, unknown>; /* not implemented! */
	}[];

	readonly productsComponentsCategories : Record<string, {
		readonly translations : Record<string, STD.translationsManager.TranslationMapping>;
	}>;

	readonly productsOptions : {
		readonly component : string;
		readonly SKU : string;
		readonly viewerGroups : {
			readonly groupName : string;
			readonly configurationName : string;
		}[];
		readonly products : string[];
		readonly iconURL : string;
		readonly translations : Record<string, STD.translationsManager.TranslationMapping>;
		readonly pricingInfo : {
			readonly pricing : BasketNS.Pricing.TPricingSerialized;
			readonly volumeComputationMethod : 'unit' | 'm' | 'm2' | 'm3';
		} | null;
		readonly  additionalData : {
			readonly RGB? : string;
			readonly SKU? : string;
		} | null;
	}[];

	readonly productsOptionsDefaults : Record</* Product.SKU */string, Record<
		/* Component.SKU */string,
		/* Option.SKU */ string
	>>;
};
type TVect3 = [ number, number, number ];

export class MPAEngineApplicationComponent<
	TComponent extends MPAEngineApplicationComponent<TComponent, TConfiguration>,
	TConfiguration extends IMPAEngineApplicationComponentConfiguration
> extends ApplicationNS.Component<TComponent, TConfiguration>
{
	static override configurationNormalize() : void {
	};

	static override initialize() {
		super.initialize.call(this);
		this.declareEventType('standDimensions-update');
		registerModuleBasket();
		registerModulePlanner();
		registerModulePlannerLayoutEditor();
		registerModulePlannerDimensionsEditor();
		registerModulePlannerFloorEditor();
		registerModulePlannerWallsEditor();
		registerModulePlannerFurniture();
		registerModuleProductChoose();
		registerModuleCustomerInformationForm();
		registerModuleCustomerInformationFormModal();
		registerModuleCommandConfirmationModal();
		registerModuleLanguageSelector();
		registerModulePlannerMenu();
		registerModuleCustomerStanding_PartitionsHeads();
		registerModuleCustomerStanding_PartitionsHeadsPrestige();
		registerModuleCustomerStanding_Plinth();
		registerModuleCustomerStanding_PersonalizationEditor();
		registerModuleCustomerStanding_modeCustomer();
		registerModuleCustomerStanding_carpetImprint();
		registerModuleCustomerStanding_carpetImprintEditor();
		registerModuleCustomerStanding_counter();
		registerModuleCustomerStanding_storage();
		registerModuleCustomerStanding_arch();
		registerModuleCustomerStanding_highSign();
		registerModuleCustomerStanding_TV();
		registerModuleCustomerStanding_Banner();
		registerModuleCustomerStanding_Lighting();
		registerModuleCustomerStanding_ObjectsPositioningDynamicsSets();
		registerModuleCustomerStanding_Furniture();
		// registerModuleCustomerStanding_StorageBanner();
		registerModuleCustomerStanding_Questions();
		registerModuleCustomerStanding_pdfAccessories();
		registerModuleCustomerStanding();
		registerModuleCustomerStanding_hangingSignCustomConfigurator();
	};

	viewer! : ViewerNS.Component;
	modals! : ModalsZone.Component;
	isReloading = false;
	isApplyingPreviousUserInputs = false;
	previousConfigurationMetadata : Awaited<ReturnType<typeof this.generateConfigurationMetadata>> | null = null;

	updateGlobalProductName() {
		if (!this.currentProduct || !this.currentProduct.reference) {
			this.component_throwLibraryError('');
		}
		const ref = this.currentProduct.reference;
		const info = this.component_configuration.products.find(x => x.SKU === ref.value);
		if (!info) {
			this.component_throwLibraryError('');
		}
		(<any> this.component_find('@header.productName')).update(info.pdfName);
	};

	readonly globalPricingContext = Object.freeze({
		input :  'exclTax',
		output : 'exclTax',
		generatePricingGetBinding(boundTo : string) {
			const [ componentPath, propertyPath ] = boundTo.split('->');
			if (!componentPath || !propertyPath) {
				throw new Error;
			}
			const propertyOperation = CM.PropertyOperation.create({
				type :       'call',
				componentPath,
				propertyPath,
				parameters : [
					{
						"$.host" : "details.host"
					}
				]
			});
			return (host : BasketNS.Product | BasketNS.Part | BasketNS.Option) => {
				const event = app.component_getEventWhichRepresentNoEvent();
				event.details.host = host;
				const key = propertyOperation.execute(app, event);
				if (typeof key !== 'string') {
					throw STD.errors.library('', {
						key,
						propertyOperation
					});
				}
				return key;
			};
		}
	});

	private readonly _basket : OMSBasketNS.Component | null = null;

	get basket() : OMSBasketNS.Component | null {
		return this._basket;
	};

	get modules() {
		return {
			all :                 EngineModule.all,
			used :                EngineModule.used,
			usedWithPageOrdered : EngineModule.usedWithPageOrdered,
			disableds :           EngineModule.disableds
		};
	};

	get currentProduct() : BasketNS.Product | null {
		if (
			app.isReloading &&
			!this.basket?.basket.currentProduct &&
			app.previousConfigurationMetadata &&
			app.previousConfigurationMetadata.basket.products.length &&
			this.basket?.basket.products.length
		)
		{
			const savedProduct = app.previousConfigurationMetadata.basket.products[ 0 ];
			if (savedProduct) {
				return this.basket?.basket.products.find(x => x.id === savedProduct.name) || null;
			}
		}
		return this.basket?.basket.currentProduct || null;
	};

	get currentProductSKU() {
		const product = this.currentProduct;
		if (!product || !product.reference) {
			this.component_throwUserError('');
		}
		return product.reference.value;
	};

	get currentDefaults() {
		const p = this.currentProduct;
		if (!p || !p.reference) {
			this.component_throwUserError('');
		}
		const defaults = this.component_configuration.productsOptionsDefaults[ p.reference.value ];
		if (!defaults) {
			this.component_throwLibraryError('');
		}
		return defaults;
	};

	hasThisModuleUsed(moduleId : string) {
		return !!this.modules.used.find(x => x.id === moduleId);
	};

	async useBasket() {
		if (this._basket) {
			throw STD.errors.user('Cannot replace application basket once created!');
		}
		const basket = <OMSBasketNS.Component> await this.component_instantiateOuterChild<OMSBasketNS.Configuration>({
			type :          'oms-basket',
			id :            'basket',
			tree :          this.component_tree,
			slot :          null,
			isSlot :        false,
			declaredSlot :  null,
			configuration : {
				application : this,
				products :    [],
				metaData :    {}
			}
		});
		await basket.component_initialize();
		(<any> this)._basket = basket;
	};

	async reload() {
		this.isReloading = true;
		if (this.clientAppParameters.app.savedBasket) {
			this.previousConfigurationMetadata =
				STD.object.clone((<any> this.clientAppParameters.app.savedBasket).metaData.replay);
		} else if (this.clientAppParameters.app.savedConfigurationCode) {
			const metadataResponse = await this.loadSavedConfigurationMetadata();
			if (!metadataResponse) {
				throw new Error('!');
			}
			const metadata = <NonNullable<MPAEngineApplicationComponent<any, any>['previousConfigurationMetadata']>> await metadataResponse.json();
			this.previousConfigurationMetadata = metadata;
		}
		if (!this.previousConfigurationMetadata) {
			throw new Error;
		}
		const metadata = this.previousConfigurationMetadata;
		const getImage = (imageReference : any) => {
			if (typeof imageReference !== 'string') {
				return (imageReference);
			}
			const res = (/<\[\[image=\x00\x00([\s\S]+)\x00\x00\]\]>/g).exec(imageReference);
			if (res === null) {
				return (imageReference);
			}
			if ((res.length < 2) || (res[ 1 ] == null)) {
				throw new Error;
			}
			const image = metadata.images[ Number.parseInt(res[ 1 ]) ];
			return (image);
		};
		const getImageAsImage = async (imageReference : any) => {
			if (typeof imageReference !== 'string') {
				return (imageReference);
			}
			const res = (/<\[\[image=\x00\x00([\s\S]+)\x00\x00\]\]>/g).exec(imageReference);
			if (res === null) {
				return (imageReference);
			}
			if ((res.length < 2) || (res[ 1 ] == null)) {
				throw new Error;
			}
			const imageSrc = <string> metadata.images[ Number.parseInt(res[ 1 ]) ];
			const image = new globalThis.Image;
			await new Promise<void>((resolve, reject) => {
				image.onload = () => resolve();
				image.onerror = reject;
				image.src = imageSrc;
			});
			return (image);
		};
		const viewerUserInput = <[ string, ViewerNS.ValueSerialized ] | undefined> this.previousConfigurationMetadata.userInputs.find(
			x => x[ 0 ] ===
				'K.visualization.V');
		if (!viewerUserInput) {
			this.component_throwLibraryError('No viewer saved data found!');
		}
		(<STD.object.SetWritable<ViewerNS.ValueSerialized, 'configurationCode'>> viewerUserInput[ 1 ]).configurationCode =
			(<any> app).previousConfigurationMetadata.basket.products[ 0 ].configurationCode;
		STD.object.visit(this.previousConfigurationMetadata.basket, (container : any, path) => {
			const customImageIndex = Number.parseInt(path.split('').reverse().join());
			if (
				Number.isNaN(customImageIndex) ||
				!path.endsWith(`currentOption.customImages.${ customImageIndex }`)
			)
			{
				return;
			}
			container.sourceFileURL = getImage(container.sourceFileURL);
			container.productionFileURL = getImage(container.productionFileURL);
			container.displayFileURL = getImage(container.displayFileURL);
		});
		if (this.hasThisModuleUsed('oms-basket')) {
			if (!this.basket) {
				this.component_throwLibraryError('');
			}
			await this.basket.reloadFromSerialized(this.previousConfigurationMetadata.basket);
		}
		this.previousConfigurationMetadata.userInputs =
			await Promise.all(this.previousConfigurationMetadata.userInputs.map(async ([ componentPath, componentUserInput ]) => {
				if (typeof componentUserInput == 'string') {
					componentUserInput = await getImageAsImage(componentUserInput);
				} else if (typeof (<any> componentUserInput)?.image === 'string') {
					(<any> componentUserInput).image = await getImageAsImage((<any> componentUserInput).image);
				}
				return (<[ string, unknown ]> [ componentPath, componentUserInput ]);
			}));
		for (const tr of this.previousConfigurationMetadata.translations) {
			STD.translationsManager.addObject(tr[ 0 ], tr[ 1 ]);
		}
	};

	override async component_initialize() {
		if (this.clientAppParameters.app.savedConfigurationCode || this.clientAppParameters.app.savedBasket) {
			this.isReloading = true;
		}
		(() => {
			// TODO : remove this dev code.
			for (const x of [ 'planner-structures-static', 'planner-structures-dynamic', 'orders-management', 'pdf', 'email' ]) {
				const idx = this.component_configuration.modules.findIndex(y => y.id === x);
				if (idx > -1) {
					this.component_configuration.modules.splice(idx, 1);
				}
			}
		})();
		await EngineModule.use(this.component_configuration.modules);
		await super.component_initialize();
		const ViewerComponentProxy = CM.getClassProxyOrThrow('viewer');
		const instances = ViewerComponentProxy.getInstances() as any as ViewerNS.Component[];
		if (instances.length !== 1) {
			this.component_throwUserError('MPAEngine should have one and only one viewer');
		}
		const viewer = instances[ 0 ] as ViewerNS.Component;
		if (!viewer || !(viewer instanceof ViewerNS.Component)) {
			this.component_throwLibraryError('Viewer component not found!', {
				viewerComponentPath : null
			});
		}
		this.viewer = viewer;
		const modals = <ModalsZone.Component> this.component_find('@.modals');
		if (!modals || !(modals instanceof ModalsZone.Component)) {
			this.component_throwLibraryError('ModalsZone component not found!');
		}
		this.modals = modals;
	};

	getScreenshotViewpoint(
		from : TViewpoint
	)
	{
		const standBB = this.computeStandBoundingBox();
		const diameter = Math.max(standBB.depth, standBB.width);
		const standCenter = {
			x : standBB.minX + (standBB.width / 2.0),
			y : standBB.minY + (standBB.height / 2.0),
			z : standBB.minZ + (standBB.depth / 2.0)
		};
		const d = diameter / .8;
		let x : number = 0;
		let y : number = standBB.height / 2.0;
		let z : number = 0;
		switch (from) {
			// TODO: top
			case 'front' :
			{
				z = d;
				break;
			}
			case 'frontLeft' :
			{
				const p0 = <[ number, number ]> [ 0, d ];
				const o = <[ number, number ]> [ standCenter.x, standCenter.z ];
				const p1 = STD.math.rotatePointOnCircle(o, p0, 45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'frontRight' :
			{
				const p0 = <[ number, number ]> [ 0, d ];
				const o = <[ number, number ]> [ standCenter.x, standCenter.z ];
				const p1 = STD.math.rotatePointOnCircle(o, p0, -45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'left' :
			{
				x = -d;
				break;
			}
			case 'right' :
			{
				x = d;
			}
				break;
			case 'back' :
			{
				z = -d;
				break;
			}
			case 'backLeft' :
			{
				const p0 = <[ number, number ]> [ 0, -d ];
				const o = <[ number, number ]> [ standCenter.x, standCenter.z ];
				const p1 = STD.math.rotatePointOnCircle(o, p0, -45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'backRight' :
			{
				const p0 = <[ number, number ]> [ 0, -d ];
				const o = <[ number, number ]> [ standCenter.x, standCenter.z ];
				const p1 = STD.math.rotatePointOnCircle(o, p0, 45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'top' :
			{
				x = standCenter.x;
				y = d * 2;
				z = standCenter.y;
				break;
			}
			default:
				throw new Error;
		}
		return {
			position : <TVect3> [ x, y, z ],
			target :   <TVect3> [ standCenter.x, standCenter.y, standCenter.z ],
			up :       <TVect3> [ 0, 1, 0 ]
		};
	};

	async update3DWithCustomerInformations() {
		if (!this.basket) {
			return;
		}
		const product = app.currentProduct;
		if(!product){
			this.component_throwUserError('');
		}
		const layoutPart = product.getPart(['layout']);
		if(!layoutPart){
			this.component_throwUserError('');
		}
		const currentOptionLayoutPart = layoutPart.currentOption;
		if(!currentOptionLayoutPart){
			this.component_throwUserError('');
		}
		if(currentOptionLayoutPart.id === 'shape-no-wall'){
			return;
		}
		if (this.currentProductSKU === 'siae') {
			const contact = this.basket.basket.billingContact;
			if (!contact || !contact.metadata) {
				this.component_throwLibraryError('');
			}
			const phModule = app.modules.used.find(x => x.id === 'standing-partitionsHeads');
			if (app.isReloading || !phModule || !(<StandingPartitionsHeadsNS.Component> phModule.component).isPartitionsHeads) {
				return;
			}
			const metadata = <any> contact.metadata;
			const defaultParams = {
				color :               '#FFFFFFFF',
				backgroundColor :     '#00000000',
				italic :              false,
				bold :                false,
				imageWidth :          1024,
				imageHeight :         1024,
				verticalAlignment :   'middle',
				horizontalAlignment : 'middle',
				size :                0,
				textOffsetY :         0,
				textOffsetX :         0,
				font :                'Oswald-Regular',
				underline :           false,
				strokeText :          false,
				strokeColor :         '',
				strokeWidth :         0,
				textAlignment :       'static',
				mode :                'horizontal',
				contents :            [ 'opacity', 'albedo' ]
			};
			await this.viewer.updateCustomTextsRealDimensions({
				...defaultParams,
				configurableMaterial : 'Salon_SIAE_name',
				materialVariation :    'Salon_SIAE_name',
				texts :                [
					{
						text : contact.companyName
					}
				],
				uvRatio :              2.8,
				surfaceWidth :         420,
				maxTextWidth :         420,
				maxTextHeight :        90
			});
			await this.viewer.updateCustomTextsRealDimensions({
				...defaultParams,
				configurableMaterial : 'Numero',
				materialVariation :    'Numero',
				texts :                [
					{
						text : `${ metadata.standPositionA.position }`
					}
				],
				uvRatio :              1.93,
				surfaceWidth :         290,
				maxTextWidth :         290,
				maxTextHeight :        100
			});
			const deliveryContact = this.basket.basket.deliveryContact;
			if (!deliveryContact) {
				this.component_throwUserError('Missing delivery contact in basket');
			}
			const countryCode = deliveryContact.countryCode;
			if (!countryCode) {
				this.component_throwUserError('Missing country code in delivery contact');
			}
			await app.viewer.setMaterialsGroups({
				values : [
					{
						groupName :         'flag',
						configurationName : countryCode
					}
				]
			});
		} else if (this.currentProductSKU === 'confort' || this.currentProductSKU === 'prestige'){
			const contact = this.basket.basket.billingContact;
			if (!contact || !contact.metadata) {
				this.component_throwLibraryError('');
			}
			const phModule = app.modules.used.find(x => x.id === 'standing-partitionsHeadsPrestige');
			if (app.isReloading || !phModule || !(<StandingPartitionsHeadsNS.Component> phModule.component).isPartitionsHeads) {
				return;
			}
			const metadata = <any> contact.metadata;
			const defaultParams = {
				color :               '#FFFFFFFF',
				backgroundColor :     '#00000000',
				italic :              false,
				bold :                false,
				imageWidth :          1024,
				imageHeight :         1024,
				verticalAlignment :   'middle',
				horizontalAlignment : 'middle',
				size :                0,
				textOffsetY :         0,
				textOffsetX :         0,
				font :                'Oswald-Regular',
				underline :           false,
				strokeText :          false,
				strokeColor :         '',
				strokeWidth :         0,
				textAlignment :       'static',
				mode :                'horizontal',
				contents :            [ 'opacity', 'albedo' ]
			};
			await this.viewer.updateCustomTextsRealDimensions({
				...defaultParams,
				configurableMaterial : 'custom_nom_exposant',
				materialVariation :    'custom_nom_exposant',
				texts :                [
					{
						text : contact.companyName
					}
				],
				uvRatio :              1,
				surfaceWidth :         420,
				maxTextWidth :         500,
				maxTextHeight :        100
			});
			await this.viewer.updateCustomTextsRealDimensions({
				...defaultParams,
				configurableMaterial : 'custom_numero',
				materialVariation :    'custom',
				texts :                [
					{
						text : `${ metadata.position }`
					}
				],
				uvRatio :              1,
				surfaceWidth :         420,
				maxTextWidth :         700,
				maxTextHeight :        200
			});
		}else if(this.currentProductSKU === 'business'){
			const contact = this.basket.basket.billingContact;
			if (!contact || !contact.metadata) {
				this.component_throwLibraryError('');
			}
			const phModule = app.modules.used.find(x => x.id === 'standing-partitionsHeads');
			if (app.isReloading || !phModule || !(<StandingPartitionsHeadsNS.Component> phModule.component).isPartitionsHeads) {
				return;
			}
			const floorPart = product.parts.find(x => x.id === 'floor');
			if(!floorPart){
				this.component_throwUserError('');
			}
			const floorPartOption = floorPart.currentOption;
			if(!floorPartOption){
				this.component_throwUserError('');
			}
			const floorPartOptionId = floorPartOption.id.replace('floor-', '').replace('-','_');
			const metadata = <any> contact.metadata;
			const defaultParams = {
				color :               '#FFFFFFFF',
				backgroundColor :     '#00000000',
				italic :              false,
				bold :                false,
				imageWidth :          1024,
				imageHeight :         1024,
				verticalAlignment :   'middle',
				horizontalAlignment : 'middle',
				size :                0,
				textOffsetY :         0,
				textOffsetX :         0,
				font :                'Oswald-Regular',
				underline :           false,
				strokeText :          false,
				strokeColor :         '',
				strokeWidth :         0,
				textAlignment :       'static',
				mode :                'horizontal',
				contents :            [ 'opacity', 'albedo' ]
			};
			await this.viewer.updateCustomTextsRealDimensions({
				...defaultParams,
				configurableMaterial : 'custom_nom_exposant',
				materialVariation :    'custom_nom_exposant',
				texts :                [
					{
						text : contact.companyName
					}
				],
				uvRatio :              1,
				surfaceWidth :         420,
				maxTextWidth :         420,
				maxTextHeight :        70
			});
			await this.viewer.updateCustomTextsRealDimensions({
				...defaultParams,
				configurableMaterial : 'custom_numero',
				materialVariation :    'custom_numero',
				texts :                [
					{
						text : `${ metadata.standPositionA.position }`
					}
				],
				uvRatio :              1,
				surfaceWidth :         420,
				maxTextWidth :         1000,
				maxTextHeight :        300
			});
			await app.viewer.setMaterialsGroups({
				values : [
					{
						groupName :         'fond_custom_numero',
						configurationName : floorPartOptionId
					}
				]
			});
		}
	};

	private async generateConfigurationMetadata() {
		if (!this.basket || !this.viewer.planner) {
			this.component_throwLibraryError('');
		}
		const basketSerialized = this.basket.basket.serialize();
		const images : string[] = [];
		const addImage = (img? : null | string | typeof Image) : null | string => {
			if (img == null) {
				return (null);
			}
			if (typeof img !== 'string') {
				throw new Error('Image is not a string!');
			}
			let idx = images.indexOf(img);
			if (idx === -1) {
				idx = images.push(img) - 1;
			}
			return (`<[[image=\x00\x00${ idx }\x00\x00]]>`);
		};
		STD.object.visit(basketSerialized, (container : any, path) => {
			const customImageIndex = Number.parseInt(path.split('').reverse().join());
			if (
				Number.isNaN(customImageIndex) ||
				!path.endsWith(`currentOption.customImages.${ customImageIndex }`)
			)
			{
				return;
			}
			container.sourceFileURL = addImage(container.sourceFileURL);
			container.productionFileURL = addImage(container.productionFileURL);
			container.displayFileURL = addImage(container.displayFileURL);
		});
		const UI = (
			(await this.getAndSerializeAllUserInputsData()).
			map(([ componentPath, componentUserInput ]) => {
				const component = app.component_find(componentPath);
				if (component instanceof ViewerGraphicsNS.Component) {
					(<any> componentUserInput).image = addImage((<ViewerGraphicsNS.ValueSerialized> componentUserInput).image);
				} else if (component instanceof InputImage.Component) {
					componentUserInput = addImage(<InputImage.ValueSerialized> componentUserInput);
				}
				return (<[ string, unknown ]> [ componentPath, componentUserInput ]);
			})
		);
		const sessionsIds = <string[]> [ ...(this.previousConfigurationMetadata?.sessionsIds || []), STD.sessionId ];
		return {
			images,
			engineVersion : ENGINE_VERSION,
			isFinal :       app.parameters.isFinal,
			userInputs :    UI,
			basket :        basketSerialized,
			sessionsIds :   sessionsIds,
			translations :  (
												Array.from(STD.translationsManager.store.entries()).
												filter(x => sessionsIds.some(y => x[ 0 ].startsWith(y)))
											)
		};
	};

	async openQuoteAndPDFGenerated() {
		const compo : CM.DOMComponent<any, any, any> | undefined = <any> this.component_find('@quoteAndPDFGenerated');
		if (!compo) {
			this.component_throwLibraryError('Component<quoteAndPDFGenerated> not found!');
		}
		await compo.component_enable();
	};

	get isUsingLocalViewer() {
		return this.parameters.useLocalViewers && this.clientAppParameters.app.isTestMode;
	};

	async closeQuoteAndPDFGenerated() {
		const compo : CM.DOMComponent<any, any, any> | undefined = <any> this.component_find('@quoteAndPDFGenerated');
		if (!compo) {
			this.component_throwLibraryError('Component<quoteAndPDFGenerated> not found!');
		}
		compo.component_stylize({
			filter :        '',
			pointerEvents : ''
		}, 'others');
		await compo.component_disable();
	};

	async processOrder() {
		if (!this.basket) {
			this.component_throwUserError('Cannot process order for an app without basket!');
		}
		if ([
			'WXNSFXVSPCALWVZR',
			'5BVQVR97NTVJ6RXH',
			'T1REM86QU1A7ZNTN',
			'N8QCIT3KPEE16XF2',
			'5TIEH3PPRY1AS9V7',
			'ODHE1WA5V6C7B4NO',
			'L7SPHJCPUZWHRQMS'
		].includes(app.clientAppParameters.app.savedConfigurationCode))
		{
			return;
		}
		const loader = (<Loader.Component> this.component_find('@loader'));
		await loader.update('basketCreation', null);
		const screenshotsToBeDoneByShape : Record<string, TViewpoint[]> = {
			'shape-none' :            [
				'frontLeft',
				'front',
				'frontRight',
				'left',
				'right',
				'backLeft',
				'back',
				'backRight',
				'top'
			],
			'shape-no-wall' :         [
				'frontLeft',
				'front',
				'frontRight',
				'left',
				'right',
				'backLeft',
				'back',
				'backRight',
				'top'
			],
			'shape-back' :            [ 'frontLeft', 'front', 'frontRight', 'left', 'right', 'backLeft', 'backRight', 'top' ],
			'shape-back-left' :       [ 'frontLeft', 'front', 'frontRight', 'right', 'backRight', 'top' ],
			'shape-back-right' :      [ 'frontLeft', 'front', 'frontRight', 'left', 'backLeft', 'top' ],
			'shape-left-back-right' : [ 'frontLeft', 'front', 'frontRight', 'top' ]
		};
		const currentProduct = this.currentProduct;
		if (!currentProduct) {
			this.component_throwUserError('');
		}
		const layoutPart = currentProduct.getPart([ 'layout' ]);
		if (!layoutPart) {
			this.component_throwUserError('');
		}
		const layoutOption = layoutPart.currentOption;
		if (!layoutOption || !layoutOption.reference) {
			this.component_throwUserError('');
		}
		const screenshotsToBeDone = screenshotsToBeDoneByShape[ layoutOption.reference.value ];
		if (!screenshotsToBeDone) {
			this.component_throwLibraryError('');
		}
		const screenshots = await screenshotsToBeDone.reduce<Promise<string[]>>(async (p, side) => {
			const a = await p;
			a.push((await this.viewer.takeSaveScreenshotGetURL({
				takeBackground : false,
				width :          512,
				height :         256,
				camera :         this.getScreenshotViewpoint(side)
			})).screenshot);
			return a;
		}, Promise.resolve([]));
		await currentProduct.setThumbnailsURLs(screenshots);
		const save = await this.viewer.saveModularConfiguration({
			screenshot : true,
			shortLink :  true,
			unique :     true
		});
		await currentProduct.setConfigurationCode(save.code);
		await currentProduct.setConfigurationReplayURL(<string> save.shortLink);
		if (!this.basket.basket.metaData) {
			this.basket.basket.metaData = {};
		}
		const md = <any> this.basket.basket.metaData;
		md.replay = await this.generateConfigurationMetadata();
		if (!this.basket.basket.deliveryContact) {
			this.component_throwLibraryError('');
		}
		md.storeLocation = {
			salon :    (<any> this.basket.basket.deliveryContact.metadata).event,
			location : (<any> this.basket.basket.deliveryContact.metadata).position
		};
		const snapshot = await this.basket.basket.proceedCheckout({
			builderType :         null,
			isQuoteRequest :      true,
			isOrderRequest :      false,
			useTwoTimesCreation : true,
			twoTimesCallback :    async (basketCode : string) => {
				await currentProduct.setConfiguratorReplayURL(STD.net.URL_format(`${ this.getAppURL().
				replace(this.code, 'replay') }/${ this.code }`, {
					basketCode,
					mode : 'client'
				}));
			}
		});
		await loader.update('processCheckout', null);
		if (!app.currentProduct || !app.currentProduct.configurationCode) {
			this.component_throwLibraryError('');
		}
		if (!this.basket.basket.deliveryContact || !this.basket.basket.deliveryContact.metadata) {
			this.component_throwLibraryError('');
		}
		await loader.update('getScreenshots', null);
		if (!currentProduct.configuratorReplayURL) {
			this.component_throwLibraryError('');
		}
		if (app.parameters.mode === 'client' || (app.parameters.forceEmail && app.clientAppParameters.app.isTestMode)) {
			await this.sendEmail(snapshot.code, currentProduct.configuratorReplayURL);
		}
		await loader.update('finalisation', null);
		const replayLinkModal = <ProductReplayLinkModalNS.Component> app.component_find('@.replayLinkModal.modal.replayLink');
		if (replayLinkModal) {
			await replayLinkModal.use(currentProduct.configuratorReplayURL);
		}
		await loader.component_disable();
	};

	async sendEmail(basketCode : string, replayLink : string) {
		// This should probably be moved to be used globally when needed ?
		const getScreenshotFromViewpoint = async (viewpoint : TViewpoint) => {
			const viewpointCamera = this.getScreenshotViewpoint(viewpoint);
			const response = await this.viewer.getScreenshot({
				transparentBackground : true,
				width :                 512,
				height :                256,
				camera :                viewpointCamera,
			});
			return response.screenshot;
		};
		const loader = (<Loader.Component> this.component_find('@loader'));
		if (!loader.component_isDisabled) {
			await loader.update('sendMail', null);
		}
		const urlToB64 = async (url : string) : Promise<string | null> => {
			const response = await fetch(url);
			if (!response.ok) {
				const errorText = await response.text();
				throw STD.errors.library(errorText, { response });
			}
			const buffer = await response.arrayBuffer();
			return STD.DOM.arrayBufferToB64(buffer, response.type);
		};

		const versionDate = (() => {
			const now = new Date();
			const minutes = (now.getMinutes() < 10 ? '0' : '') + now.getMinutes();
			if (this.currentLanguage === 'fr') {
				return `Version du ${ now.toLocaleDateString('fr') } à ${ now.getHours() }h${ minutes }`;
			} else {
				return `Version of the ${ now.toLocaleDateString('en') } at ${ now.getHours() }h${ minutes }`;
			}
		})();

		const currentProduct = this.currentProduct;
		if (!currentProduct || !currentProduct.reference) {
			this.component_throwUserError('Missing current product!');
		}
		const currentProductConf = this.component_configuration.products.find(x => x.SKU === currentProduct.id);
		if (!currentProductConf) {
			this.component_throwUserError('Missing current product in product available');
		}
		const pdfName = currentProductConf.pdfName;
		const route = this.clientAppParameters.server.isOnProd ?
			`${ location.origin }/${ this.currentLanguage }/standing/stand2024/sendEmail` :
			this.isUsingLocalViewer ? `/${ this.currentLanguage }/standing/stand2024/sendEmail` :
				`https://emersya.com/${ this.currentLanguage }/standing/stand2024/sendEmail`
		;
		const response = await fetch(route, {
			method :  'POST',
			headers : {
				'Content-Type' : 'application/json'
			},
			body :    JSON.stringify({
				isTest :      this.clientAppParameters.app.isTestMode,
				texts :       {
					productFullName :        (<any> currentProductConf.translations[ 'fullName' ])[ this.currentLanguage ],
					replayAndShare :         (<any> this.component_configuration.pdfTranslations[ 'replayAndShare' ])[ this.currentLanguage ],
					order :                  (<any> this.component_configuration.pdfTranslations[ 'order' ])[ this.currentLanguage ],
					orderDetail :            (<any> this.component_configuration.pdfTranslations[ 'orderDetail' ])[ this.currentLanguage ],
					configuration :          (<any> this.component_configuration.pdfTranslations[ 'order' ])[ this.currentLanguage ],
					profil :                 (<any> this.component_configuration.pdfTranslations[ 'profil' ])[ this.currentLanguage ],
					details :                (<any> this.component_configuration.pdfTranslations[ 'details' ])[ this.currentLanguage ],
					htpriceinfo :            (<any> this.component_configuration.pdfTranslations[ 'htpriceinfo' ])[ this.currentLanguage ],
					totalPrice :             (<any> this.component_configuration.pdfTranslations[ 'totalPrice' ])[ this.currentLanguage ],
					included :               (<any> this.component_configuration.pdfTranslations[ 'included' ])[ this.currentLanguage ],
					options :                (<any> this.component_configuration.pdfTranslations[ 'options' ])[ this.currentLanguage ],
					priceWithoutVAT :        (<any> this.component_configuration.pdfTranslations[ 'priceWithoutVAT' ])[ this.currentLanguage ],
					complementaries :        (<any> this.component_configuration.pdfTranslations[ 'complementaries' ])[ this.currentLanguage ],
					'finalize.teamContact' : (<any> this.component_configuration.pdfTranslations[ 'finalize.teamContact' ])[ this.currentLanguage ],
					'finalize.other' :       (<any> this.component_configuration.pdfTranslations[ 'finalize.other' ])[ this.currentLanguage ],
					salon :                  (<any> this.component_configuration.pdfTranslations[ 'salon' ])[ this.currentLanguage ],
					location :               (<any> this.component_configuration.pdfTranslations[ 'clientLocation' ])[ this.currentLanguage ],
					company :                (<any> this.component_configuration.pdfTranslations[ 'company' ])[ this.currentLanguage ],
					firstname :              (<any> this.component_configuration.pdfTranslations[ 'firstname' ])[ this.currentLanguage ],
					lastname :               (<any> this.component_configuration.pdfTranslations[ 'lastname' ])[ this.currentLanguage ],
					mail :                   (<any> this.component_configuration.pdfTranslations[ 'mail' ])[ this.currentLanguage ],
					phone :                  (<any> this.component_configuration.pdfTranslations[ 'phone' ])[ this.currentLanguage ],
					address :                (<any> this.component_configuration.pdfTranslations[ 'address' ])[ this.currentLanguage ],
					city :                   (<any> this.component_configuration.pdfTranslations[ 'city' ])[ this.currentLanguage ],
					postalCode :             (<any> this.component_configuration.pdfTranslations[ 'postalCode' ])[ this.currentLanguage ],
					country :                (<any> this.component_configuration.pdfTranslations[ 'country' ])[ this.currentLanguage ],
					versionDate :            versionDate,
					textMail :               (<any> this.component_configuration.pdfTranslations[ 'textMail' ])[ this.currentLanguage ],
					replayAndShareMail :     (<any> this.component_configuration.pdfTranslations[ 'replayAndShareMail' ])[ this.currentLanguage ],
					clientInformation :      (<any> this.component_configuration.pdfTranslations[ 'clientInformation' ])[ this.currentLanguage ],
					nonContractual3DViews :  (<any> this.component_configuration.pdfTranslations[ 'nonContractual3DViews' ])[ this.currentLanguage ]
				},
				images :      {
					logo :      await urlToB64(app.clientAppParameters.methodsStore.getRemoteAssetFileURL('logo.png')),
					smallLogo : await urlToB64(app.clientAppParameters.methodsStore.getRemoteAssetFileURL('icon-s.png'))
				},
				screenshots : {
					face : await getScreenshotFromViewpoint('front'),
					top :  await getScreenshotFromViewpoint('top'),
				},
				fonts :       {
					Iconia :             await urlToB64('/bundles/EmersyaWebsitePro/fonts/Iconia/IconiaFull.otf'),
					GothamBold :         await urlToB64('/bundles/EmersyaWebsitePro/fonts/apps/standing/Gotham-Bold.otf'),
					GothamBook :         await urlToB64('/bundles/EmersyaWebsitePro/fonts/apps/standing/Gotham-Book.otf'),
					GothamMedium :       await urlToB64('/bundles/EmersyaWebsitePro/fonts/apps/standing/Gotham-Medium.otf'),
					GothamLight :        await urlToB64('/bundles/EmersyaWebsitePro/fonts/apps/standing/Gotham-Light.otf'),
					GothamLightItalic :  await urlToB64('/bundles/EmersyaWebsitePro/fonts/apps/standing/Gotham-LightItalic.otf'),
					GothamMediumItalic : await urlToB64('/bundles/EmersyaWebsitePro/fonts/apps/standing/Gotham-MediumItalic.otf')
				},
				basketCode :  basketCode,
				pdfName :     pdfName,
				replayLink :  replayLink
			})
		});
		if (!response.ok) {
			const errorText = await response.text();
			try {
				const emersyaError = JSON.parse(errorText);
				throw STD.errors.library(emersyaError.description, { response, emersyaError });
			} catch (err) {
				throw STD.errors.library(errorText, { response });
			}
		}
	};

	get width() {
		const p = app.currentProduct;
		if (!p) {
			return 0;
		}
		const dpart = p.getPart([ 'dimensions' ]);
		const width = Number.parseFloat(dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1);
		return width;
	};

	get depth() {
		const p = app.currentProduct;
		if (!p) {
			return 0;
		}
		const dpart = p.getPart([ 'dimensions' ]);
		const depth = Number.parseFloat(dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1);
		return depth;
	};

	get standSurface() {
		const p = app.currentProduct;
		if (!p) {
			return 0;
		}
		const dpart = p.getPart([ 'dimensions' ]);
		const width = Number.parseFloat(dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1);
		const depth = Number.parseFloat(dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1);
		return width * depth;
	};

	async onPlannerUpdated(ev : STD.events.Event<any, CM.Component<any, any>>) {
		void ev;
		await Promise.resolve();
	};

	updatePrice(ev : STD.events.Event<any, CM.Component<any, any>>) {
		const newPrice : BasketNS.Price = ev.details.price;
		const exclTax : STD.math.BigNumber = newPrice.exclTax;
		const priceComponent : Text.Component | undefined = <any> this.component_find('@quoteManagement.priceMetadata.price');
		if (!priceComponent) {
			this.component_throwLibraryError('priceComponent not found!', {});
		}
		const price = (Math.trunc(STD.math.round(exclTax.toNumber(), 3) * 100) / 100).toFixed(2);
		priceComponent.update(price.toString());
	};

	override async component_start(ev : STD.events.Event<any, CM.Component<any, any>>) {
		// Disable rotate button on tv products because there are against wall and so, unrotatable.
		for (const SKU of [ '17PB01A', '17PB01B', '17PB01E', '17PB01D' ]) {
			let ext = this.viewer.productsExtensionById[ SKU ];
			if (!ext) {
				ext = this.viewer.productsExtensionById[ SKU ] = {
					quantity : null
				};
			}
			if (!ext.disabledButtons) {
				ext.disabledButtons = [];
			}
			ext.disabledButtons.push('rotate');
		}
		if (this.basket) {
			// Mais à quoi sert ce code ???!
			const debouncer = STD.misc.createDebouncerPromise({
				delay : 50,
				fn :    this.onBasketPartReferenceUpdated.bind(this)
			});
			this.basket.addEventListener(
				'basket-update',
				async (...x : any[]) => {
					const prom = debouncer(...x);
					return (await new Promise<unknown>((res, rej) => {
						let promEnded = false;
						setTimeout(() => {
							if (!promEnded) {
								res(void 1);
							}
						}, 1000);
						prom.then((...result : any[]) => {
							promEnded = true;
							res(result);
						}).catch(rej);
					}));
					// const res = await debouncer(...x);
					// return (res);
					// STD.misc.createDebouncerPromise({
					// 	delay : 50,
					// 	fn : this.onBasketPartReferenceUpdated.bind(this)
					// }),
				},
				{
					listener :   this,
					subType :    'partReferenceUpdated',
					isAtTarget : true
				}
			);
		}
		// this.viewer.addEventListener(
		// 	'message',
		// 	this.onMeshHighlight.bind(this),
		// 	{
		// 		listener :   this,
		// 		isAtTarget : true,
		// 		subType :    'onMeshHighlight'
		// 	}
		// );
		if (this.isReloading) {
			const CAP = this.clientAppParameters;
			if (!CAP.server.isOnProd) {
				(<any> this.clientAppParameters.server).webserviceURL =
					`https://${ CAP.app.isTestMode ? 'testws' : 'ws' }.emersya.com/ws`;
			}
			if (CAP.app.savedConfigurationCode === 'WXNSFXVSPCALWVZR') {
				(<any> CAP.app).savedConfigurationCode = '5BVQVR97NTVJ6RXH';
			}
			if (CAP.app.savedConfigurationCode === '5BVQVR97NTVJ6RXH') {
				(<any> CAP.app).savedConfigurationCode = 'T1REM86QU1A7ZNTN';
			}
			if (CAP.app.savedConfigurationCode === 'T1REM86QU1A7ZNTN') {
				(<any> CAP.app).savedConfigurationCode = 'N8QCIT3KPEE16XF2';
			}
			if (CAP.app.savedConfigurationCode === 'N8QCIT3KPEE16XF2') {
				(<any> CAP.app).savedConfigurationCode = '5TIEH3PPRY1AS9V7';
			}
			if (CAP.app.savedConfigurationCode === '5TIEH3PPRY1AS9V7') {
				(<any> CAP.app).savedConfigurationCode = 'ODHE1WA5V6C7B4NO';
			}
			if (CAP.app.savedConfigurationCode === 'ODHE1WA5V6C7B4NO') {
				(<any> CAP.app).savedConfigurationCode = 'L7SPHJCPUZWHRQMS';
			}
			await this.reload();
			if (!this.previousConfigurationMetadata) {
				this.component_throwLibraryError('');
			}
			await super.component_start(ev);
			this.isApplyingPreviousUserInputs = true;

			// await this.component_find('@visualization.V').component_start(ev);

			const userInputs : [ CM.Path.TPathRaw, unknown ][] = (
				this.previousConfigurationMetadata.userInputs.filter(x => !x[ 0 ].includes(
					'K.modules:panel.standing-counter.content:colors')).
				map(x => {
					if (x[ 0 ].startsWith('K.customerInformationForm.modal.form')) {
						return [
							x[ 0 ].replace(
								'K.customerInformationForm.modal.form',
								'K.modals.oms-customerModal:content.form'
							),
							x[ 1 ]
						];
					} else {
						return x;
					}
				})
			);
			const defaultGetNewOrder = (
				path : string,
				boundComponent : CM.Component<any, any>,
				currentOrder : number
			) => {
				if (path === 'K.visualization.V') {
					return 0;
				}
				void path;
				void boundComponent;
				return currentOrder + 100;
			};
			const getNewOrder = this.component_configuration.replayUserInputsSorter || defaultGetNewOrder;
			let toBeDeserialized = Array.from(userInputs).filter(x => (
				!x[ 0 ].startsWith('K.visualization.V.viewer-menu') &&
				!x[ 0 ].startsWith('K.customConfigurators')
			));
			let hasDoneChanges = true;
			while (toBeDeserialized.length && hasDoneChanges) {
				hasDoneChanges = false;
				const {
								remaining,
								toDeserializeThisStep
							} = toBeDeserialized.reduce<{
					readonly remaining : typeof toBeDeserialized;
					readonly toDeserializeThisStep : [ string, Input.Component<any, any, any, any, any>, number, unknown ][];
				}>((a, [ path, value ], i) => {
					const component = this.component_findMaybe(path);
					if (!component) {
						a.remaining.push([ path, value ]);
					} else if (!(component instanceof Input.Component)) {
						this.component_throwUserError(
							`Given user input do not match current tree, component<${ path }> is not an input!`,
							{
								userInputs,
								path,
								value,
								component
							}
						);
					} else {
						a.toDeserializeThisStep.push([ path, component, getNewOrder(path, component, i + 1000), value ]);
					}
					return a;
				}, {
					remaining :             [],
					toDeserializeThisStep : []
				});
				toBeDeserialized = remaining;
				toDeserializeThisStep.sort((a, b) => a[ 2 ] - b[ 2 ]);
				if (toDeserializeThisStep.length) {
					hasDoneChanges = true;
				}
				for (const [ unused0, component, unused1, value ] of toDeserializeThisStep) {
					if ((<any> value)?.disableMPAEngineAutoDeserialization || component.static.type === 'viewer-graphics') {
						continue;
					}
					void [ unused0, unused1 ];
					await component.updateValue(await component.deserialize(value));
				}
			}
			if (toBeDeserialized.length) {
				this.component_throwLibraryError('Cannot deserialize all data!', {
					undeserializable : toBeDeserialized
				});
			}
			this.isApplyingPreviousUserInputs = false;
			// await super.component_start(ev);
			// this.isApplyingPreviousUserInputs = true;
			// // await this.component_find('@visualization.V').component_start(ev);
			// await this.setAndDeserializeAllUserInputsData(this.previousConfigurationMetadata.userInputs.filter(x => !x[ 0 ].includes(
			// 	'K.modules:panel.standing-counter.content:colors')));
			// this.isApplyingPreviousUserInputs = false;
			this.isReloading = false;
		} else {
			await this.component_find('@visualization.V').component_start(ev);
			await super.component_start(ev);
		}




		await this.evaluateModulesConditions();
		const customerMode = app.modules.used.find(x => x.id === 'standing-customerMode');
		if (customerMode) {
			await (<StandingCustomerModeNS.Component> customerMode.component).onAppReady();
		}
		await this.component_find('@.loader').component_disable();
	};

	getFloorThickness() {
		return 0.01;
	};

	isChangingProduct = false;

	async disableLoaderWhenAppReady(whatAsBeenUpdated : string) {
		this.awaitedToBeReady.delete(whatAsBeenUpdated);
		if (!this.awaitedToBeReady.size) {
			const loader = this.component_find('@loader') as CM.Component.TAny;
			await this.onAppReady();
			await loader.component_disable();
		}
	};

	private async evaluateModulesConditions() {
		for (const module of
			this.modules.used.filter<EngineModule.UIEngineModule<any>>(<(value : any) => value is EngineModule.UIEngineModule<any>> (x => x.type ===
				'ui')))
		{
			await module.evaluateConditions();
		}
	};

	private transactionCount = 0;

	onChangeViewer(event : STD.events.Event<any, any>) {
		const d = <CM.DOMComponent<any, any, any>> this.component_find('K.visualization.V.loader');
		if (event.subType === 'start') {
			d.component_DOM.classList.add('viewerChanging');
		} else {
			d.component_DOM.classList.remove('viewerChanging');
		}
	};


	onKeyDown(event : Event) {

		const modals = <CM.DOMComponent<any, any, any>>app.component_find('@modals');
		if (
			!this.transactionCount ||
			modals.component_DOM.contains(event.target) // Customer infos modal is shown during a transaction so user should be able to fill it nether less
		) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
	};

	sequential = (() => {
		const sequence : (() => Promise<unknown> | unknown)[] = [];
		return async function <TReturn = void>(
			this : MPAEngineApplicationComponent<any, any>,
			fn : () => Promise<TReturn> | TReturn
		) : Promise<TReturn> {
			sequence.push(fn);
			await STD.misc.poll(
				() => (sequence[ 0 ] === fn),
				5,
				15 * 1000
			);
			let r : TReturn;
			try {
				r = await fn();
			} catch (error : any) {
				sequence.shift();
				throw error;
			}
			sequence.shift();
			return r;
		};


	})()

	async transaction(fn : () => Promise<void> | void) {
		if (this.transactionCount === 0) {
			document.documentElement.style.pointerEvents = 'none';
			if (this.viewer.isLoaded()) {
				await app.viewer.disableMouseButtons();
				await app.viewer.disableTouchInteractions();
			}
			await this.component_find('K.visualization.V.loader').component_enable();
		}
		this.transactionCount++;
		let error;
		try {
			await fn();
		} catch (_error : any) {
			error = _error;
		}
		this.transactionCount--;
		if (this.transactionCount === 0) {
			await this.component_find('K.visualization.V.loader').component_disable();
			document.documentElement.style.pointerEvents = '';
			if (this.viewer.isLoaded()) {
				await this.viewer.enableMouseButtons();
				await this.viewer.enableTouchInteractions();
			}
		}
		if (error) {
			throw error;
		}
	};

	private customerInformationFormPromise : Promise<void> | null = null;

	async callWS() {

	};

	updateWallsNameWithSize() {
		if (!this.viewer.planner) {
			this.component_throwLibraryError('');
		}
		const CP = this.currentProduct;
		const wallCat = this.viewer.categories.find(x => x.name === 'Wall');
		if (!wallCat || !CP || !CP.reference) {
			this.component_throwLibraryError('');
		}
		const prodRef = CP.reference;
		if (!prodRef) {
			this.component_throwLibraryError('');
		}
		const wallProd = wallCat.products.find(x => x.SKU === `@wall-${ prodRef.value }`);
		if (!wallProd) {
			this.component_throwLibraryError('');
		}
		const walls = Array.from(wallProd.instances);
		const wdh = this.getWDH();
		const trs = this.modules.used.find(x => x.id === 'viewer-planner-walls-editor')?.translations;
		if (!trs) {
			this.component_throwLibraryError('');
		}
		if (!trs.wall) {
			this.component_throwLibraryError('');
		}
		for (const wall of walls) {
			const trWallName = trs[ `viewer-planner-walls-editor-${ wall.metadata.id }` ];
			if (!trWallName) {
				this.component_throwLibraryError('');
			}
			const prefix = `${ trs.wall[ this.currentLanguage ] } : ${ trWallName[ this.currentLanguage ] }`;
			let name;
			if (wall.metadata.id === 'left') {
				name = `${ prefix } : ${ wdh.depth * wdh.height } m²`;
			} else if (wall.metadata.id === 'back') {
				name = `${ prefix } : ${ wdh.width * wdh.height } m²`;
			} else if (wall.metadata.id === 'right') {
				name = `${ prefix } : ${ wdh.depth * wdh.height } m²`;
			}
			if (name) {
				wall.setName(name);
				if (wall.basketPart) {
					(<any> wall.basketPart).title = name;
				}
			}
		}
	};

	async basketUpdate(event : STD.events.Event<any, CM.Component<any, any>>) {
		if (this.isReloading && ((!this.currentProduct && event.subType !== 'productUsed') || !app.viewer.isLoaded())) {
			return;
		}
		await this.transaction(async () => {
			if (!this.basket || event.subType === 'option-volume-updated') {
				return;
			}
			const batch : Viewer.IBatchDescriptor = [];
			if (event.subType === 'productUsed') {
				this.isChangingProduct = true;
				if (!app.isReloading) {
					if (this.hasThisModuleUsed('oms-customerModal') && app.parameters.mode !== 'client') {
						const modalZone = <ModalsZone.Component> this.component_find('@.modals');
						const customerInformationModal = <OMSCustomerModalNS.Component> this.component_find('@.modals.oms-customerModal');
						const customerForm = <OMSCustomerFormComponent>customerInformationModal.component_find('@:content.form');
						await customerForm.updateSalon();
						// this.customerInformationFormPromise = <Promise<void>> <any> customerInformationModal.use();
						await modalZone.useModal(customerInformationModal, null);
					}
					if (this.hasThisModuleUsed('standing-partitionsHeads')) {
						await (<StandingPartitionsHeadsNS.Component> app.component_find('@modules:panel.standing-partitionsHeads.content')).remove();
					}
					if (this.hasThisModuleUsed('standing-partitionsHeadsPrestige')) {
						await (<StandingPartitionsHeadsPrestigeNS.Component> app.component_find('@modules:panel.standing-partitionsHeadsPrestige.content')).remove();
					}
					if (this.hasThisModuleUsed('standing-tv')) {
						await (<StandingTVNS.Component> app.component_find('K.standing-tv')).remove();
					}
					if (this.hasThisModuleUsed('standing-lighting')) {
						await (<StandingLightingNS.Component> app.component_find('K.standing-lighting')).remove();
					}
					if (this.hasThisModuleUsed('standing-highSign')) {
						await (<StandingHighSignNS.Component> app.component_find('@modules:panel.standing-highSign.content')).onProductChange_before();
					}
					if (this.hasThisModuleUsed('standing-banner')) {
						await (<StandingBannerNS.Component> app.component_find('@modules:panel.standing-banner.content')).onProductChange_before();
					}
					if (this.hasThisModuleUsed('standing-storage')) {
						await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).onProductChange_before();
						await (<StandingBannerConfortNS.Component> app.component_find('@modules:panel.standing-storage.content:storageBannerConfort')).onProductChange_before();
					}
					if (this.hasThisModuleUsed('standing-arch')) {
						await (<StandingArchNS.Component> app.component_find('@modules:panel.standing-arch.content')).onProductChange_before();
					}
					if (this.hasThisModuleUsed('standing-carpetImprint')) {
						await (<StandingCarpetImprintNS.Component> app.component_find('K.standing-carpetImprint')).remove();
					}
					if (this.hasThisModuleUsed('standing-plinth')) {
						await (<StandingPlinthNS.Component> app.component_find('K.standing-plinth')).remove();
					}
					if (this.hasThisModuleUsed('standing-counter')) {
						await (<StandingCounterNS.Component> app.component_find('@modules:panel.standing-counter.content')).onProductChange_before();
					}
					const numericOptionsTile = <StandingNumericOptionsTileNS.Component>this.component_findMaybe('K.modals.modalNumericOptions:content.options');
					if(numericOptionsTile){
						await numericOptionsTile.onProductChange_before();
					}
					if (!this.isReloading) {
						if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
							await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).clear();
						}
					}
					if (this.viewer.planner /* atchoum */ && app.viewer.isLoaded()) {
						for (const instance of Array.from(this.viewer.safe.value.productInstances)) {
							await instance.delete();
						}
					}
					if (this.basket.basket.currentProduct) {
						if (this.isReloading) {
							await this.viewer.updateValue({
								baseLocalId :       2,
								productInstances :  new Set,
								configurationCode : this.previousConfigurationMetadata?.basket.products[ 0 ]?.configurationCode || '',
								viewerCode :        this.basket.basket.currentProduct.viewerCode
							});
						} else {
							await this.viewer.updateValue({
								baseLocalId :       2,
								productInstances :  new Set,
								configurationCode : null,
								viewerCode :        this.basket.basket.currentProduct.viewerCode
							});
						}
					}
				}
				const viewerLoader = <CM.DOMComponent<any, any, any>> app.component_find('K.visualization.V.loader');
				viewerLoader.component_DOM.classList.add('viewerChanging');
				this.updateGlobalProductName();
				if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
					await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).checkWallCustomImageDisplay();
				}
				if (this.hasThisModuleUsed('viewer-planner-furniture-editor')) {
					await (<PlannerFurnitureEditorNS.Component> app.component_find(
						'@modules:panel.viewer-planner-furniture-editor.content')).update();
				}
				if (this.hasThisModuleUsed('standing-carpetImprint')) {
					await (<StandingCarpetImprintNS.Component> app.component_find('K.standing-carpetImprint')).preloadAfterGlobalProductChanged();
				}
				if (this.hasThisModuleUsed('standing-partitionsHeads')) {
					await (<StandingPartitionsHeadsNS.Component> app.component_find('@modules:panel.standing-partitionsHeads.content')).insert();
				}
				if (this.hasThisModuleUsed('standing-partitionsHeadsPrestige')) {
					await (<StandingPartitionsHeadsPrestigeNS.Component> app.component_find('@modules:panel.standing-partitionsHeadsPrestige.content')).insert();
				}
				if (this.hasThisModuleUsed('standing-counter')) {
					await (<StandingCounterNS.Component> app.component_find('@modules:panel.standing-counter.content')).onProductChange_after();
				}
				if (this.hasThisModuleUsed('standing-arch')) {
					await (<StandingArchNS.Component> app.component_find('@modules:panel.standing-arch.content')).onProductChange_after();
				}
				if (this.hasThisModuleUsed('standing-highSign')) {
					await (<StandingHighSignNS.Component> app.component_find('@modules:panel.standing-highSign.content')).onProductChange_after();
				}
				if (this.hasThisModuleUsed('standing-plinth')) {
					await (<StandingPlinthNS.Component> app.component_find('K.standing-plinth')).insert();
				}
				if (this.hasThisModuleUsed('standing-lighting')) {
					await (<StandingLightingNS.Component> app.component_find('K.standing-lighting')).insert();
				}
				if (this.hasThisModuleUsed('viewer-planner-floor-editor')) {
					await (<PlannerFloorEditorNS.Component> app.component_find('@modules:panel.viewer-planner-floor-editor.content')).reset(
						event);
				}
				if (this.hasThisModuleUsed('standing-tv')) {
					await (<StandingTVNS.Component> app.component_find('K.standing-tv')).insert();
				}
				if (this.hasThisModuleUsed('standing-personalizationEditor')) {
					await (<StandingPersonalizationEditorNS.Component> app.component_find(
						'@modules:panel.standing-personalizationEditor.content')).reset(
						event);
				}
				if (this.hasThisModuleUsed('standing-carpetImprintEditor')) {
					await (<StandingCarpetImprintEditorNS.Component> app.component_find(
						'@modules:panel.standing-carpetImprintEditor.content')).instantiateMenu(
						event);
				}
				if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
					await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).instantiateMenu(
						event);
				}
				if (this.hasThisModuleUsed('viewer-planner-layout-editor')) {
					const c = (<PlannerLayoutEditorNS.Component> app.component_find(
						'@modules:panel.viewer-planner-layout-editor.content'));
					c.component_configuration.defaultValue = 'shape-back-right';
					await c.reset();
				}
				if (this.hasThisModuleUsed('viewer-planner-dimensionsEditor')) {
					await (<PlannerDimensionsEditorNS.Component> app.component_find(
						'@modules:panel.viewer-planner-dimensionsEditor.content')).reset();
				}
				const layoutModule = app.modules.used.find(x => x.id === 'viewer-planner-layout-editor');
				if (layoutModule && layoutModule.isUsed && layoutModule.component) {
					await (<InputSelect.Component> layoutModule.component).updateValueFromId('shape-back-right');
				}
				this.customerInformationFormPromise = null;
				this.updateWallsNameWithSize();
				viewerLoader.component_DOM.classList.remove('viewerChanging');
				this.isChangingProduct = false;
				if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
					await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).update();
				}
				if (this.hasThisModuleUsed('oms-customerForm')) {
					try {
						(<OMSCustomerFormButtonNS.Component> this.component_find('K.visualization.topRightMenu.customerInformationButton')).component_DOM.classList.remove(
							'notReady');
					} catch (error) {
					}
				}
				// ICI MATTHIS EXPLICATION
				// if(['prestige', 'confort'].includes(this.currentProduct?.id || '')){
				// 	VAR C = get Compo From Path "K.modules:sectionsIcons.standing-partitionsHeads.icon" + ;
				// 	if !c error
				// 	c
				// }else{

				// }


			} else if (event.details.part) {
				const path = event.details.part.getPath().replace(event.details.product.id, '');
				if (path.startsWith('.dimensions.')) {
					if (!this.viewer.planner) {
						this.component_throwLibraryError('');
					}
					await this.viewer.planner.updateSize({
						[ event.details.part.id ] : Number.parseFloat(event.details.reference.value)
					});
					if (this.hasThisModuleUsed('standing')) {
						if (!this.currentProduct) {
							this.component_throwUserError('Missing current product while dimensions updated');
						}
						const dpart = this.currentProduct.getPart([ 'dimensions' ]);
						const width = dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1;
						const depth = dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1;
						(<Text.Component> app.component_find('K.visualization.topRightMenu.dimensions')).update(width +
							'm x ' +
							depth +
							'm');
					}
					if (this.hasThisModuleUsed('viewer-planner-furniture-editor')) {
						await (<PlannerFurnitureEditorNS.Component> app.component_find(
							'@modules:panel.viewer-planner-furniture-editor.content')).updateDisclaimer();
						await (<PlannerFurnitureEditorNS.Component> app.component_find(
							'@modules:panel.viewer-planner-furniture-editor.content')).changeLanguageLink();
					}
					if (this.hasThisModuleUsed('standing-pdfAccessories')) {
						await (<StandingPDFAccessoriesNS.Component> app.component_find(
							'K.visualization.topRightMenu.standing-pdfAccessories')).changeLanguageLink();
					}
					if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
						await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).updateSize();
					}
					if (this.hasThisModuleUsed('standing-carpetImprint')) {
						await (<StandingCarpetImprintNS.Component> app.component_find('K.standing-carpetImprint')).update();
					}
					if (this.hasThisModuleUsed('standing-counter') && !app.isReloading) {
						await (<StandingCounterNS.Component> app.component_find('@modules:panel.standing-counter.content')).update();
					}
					if (this.hasThisModuleUsed('standing-partitionsHeads')) {
						await (<StandingPartitionsHeadsNS.Component> app.component_find('@modules:panel.standing-partitionsHeads.content')).updateOrRemove();
					}
					if (this.hasThisModuleUsed('standing-partitionsHeadsPrestige')) {
						await (<StandingPartitionsHeadsPrestigeNS.Component> app.component_find('@modules:panel.standing-partitionsHeadsPrestige.content')).update();
					}
					if (this.hasThisModuleUsed('standing-arch')) {
						await (<StandingArchNS.Component> app.component_find('@modules:panel.standing-arch.content')).update();
					}
					if (this.hasThisModuleUsed('standing-storage')) {
						// await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).update();
						// await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).updateBanner();
						await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).checkUpdate();
						await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).updateDisclaimer();
						await (<StandingBannerConfortNS.Component> app.component_find('@modules:panel.standing-storage.content:storageBannerConfort')).update();
						const product = this.currentProduct;
						if(!product){
							this.component_throwUserError('');
						}
						await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).updateDisclaimerBanner(product.id);
					}
					if (this.hasThisModuleUsed('standing-plinth')) {
						await (<StandingPlinthNS.Component> app.component_find('K.standing-plinth')).update();
					}
					if (this.hasThisModuleUsed('standing-lighting')) {
						await (<StandingLightingNS.Component> app.component_find('K.standing-lighting')).update();
					}
					if (this.hasThisModuleUsed('standing-highSign')) {
						await (<StandingHighSignNS.Component> app.component_find('@modules:panel.standing-highSign.content')).update();
					}
					if (this.hasThisModuleUsed('standing-banner')) {
						await (<StandingBannerNS.Component> app.component_find('@modules:panel.standing-banner.content')).update();
					}
					if (this.hasThisModuleUsed('standing-tv')) {
						await (<StandingTVNS.Component> app.component_find('K.standing-tv')).update();
					}
					if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
						await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).updateSize();
					}
					this.updateWallsNameWithSize();
					if (this.hasThisModuleUsed('standing-objectsPositioningDynamicsSets')) {
						await (<StandingObjectsPositioningDynamicsSetsNS.Component> app.component_find(
							'@standing-objectsPositioningDynamicsSets')).refreshTvPositions();
					}
				} else if (path.startsWith('.floor')) {
					const option = event.details.part.currentOption;
					if (!app.viewer.planner) {
						this.component_throwLibraryError('');
					}
					app.viewer.planner.msSetting = option.metaData.viewerGroups[ 0 ].configurationName;
					await app.viewer.setMaterialsGroups({
						values : option.metaData.viewerGroups
					});
					await app.viewer.planner.updateGroundUV();
				} else if (path.startsWith('.layout')) {
					await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).update();
					if (this.hasThisModuleUsed('standing-carpetImprint')) {
						await (<StandingCarpetImprintNS.Component> app.component_find('K.standing-carpetImprint')).update();
					}
					if (this.hasThisModuleUsed('standing-counter') && !this.isReloading) {
						await (<StandingCounterNS.Component> app.component_find('@modules:panel.standing-counter.content')).reset();
					}
					if (this.hasThisModuleUsed('standing-partitionsHeads')) {
						await (<StandingPartitionsHeadsNS.Component> app.component_find('@modules:panel.standing-partitionsHeads.content')).updateOrRemove();
					}
					if (this.hasThisModuleUsed('standing-partitionsHeadsPrestige')) {
						await (<StandingPartitionsHeadsPrestigeNS.Component> app.component_find('@modules:panel.standing-partitionsHeadsPrestige.content')).update();
					}
					if (this.hasThisModuleUsed('standing-arch')) {
						await (<StandingArchNS.Component> app.component_find('@modules:panel.standing-arch.content')).update();
					}
					if (this.hasThisModuleUsed('standing-storage')) {
						await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).checkUpdate();
						await (<StandingBannerConfortNS.Component> app.component_find('@modules:panel.standing-storage.content:storageBannerConfort')).update();
						// await (<StandingStorageNS.Component> app.component_find('@modules:panel.standing-storage.content')).updateBanner();
					}
					if (this.hasThisModuleUsed('standing-plinth')) {
						await (<StandingPlinthNS.Component> app.component_find('K.standing-plinth')).update();
					}
					if (this.hasThisModuleUsed('standing-furniture')) {
						await (<StandingFurnitureNS.Component> app.component_find('K.standing-furniture')).insert();
					}
					if (this.hasThisModuleUsed('standing-lighting')) {
						await (<StandingLightingNS.Component> app.component_find('K.standing-lighting')).update();
					}
					if (this.hasThisModuleUsed('standing-highSign')) {
						await (<StandingHighSignNS.Component> app.component_find('@modules:panel.standing-highSign.content')).updateWhenLayoutChanges();
					}
					if (this.hasThisModuleUsed('standing-banner')) {
						await (<StandingBannerNS.Component> app.component_find('@modules:panel.standing-banner.content')).update();
					}
					if (this.hasThisModuleUsed('standing-tv')) {
						await (<StandingTVNS.Component> app.component_find('K.standing-tv')).update();
					}
					// if (this.hasThisModuleUsed('viewer-planner-walls-editor') && !this.isReloading) {
					// yes we need to update 2 times... First time storage and partitions heads was not ready...
					await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).update();
					// }
					await this.customerInformationFormPromise;
					await this.update3DWithCustomerInformations();
					this.updateWallsNameWithSize();
					const option = event.details.part.currentOption;
					if (option.id === 'shape-no-wall') {
						for (const SKU of [ '17PB01A', '17PB01B', '17PB01E', '17PB01D' ]) {
							const furnitureComponent = this.component_findMaybe(`@.modules:panel.viewer-planner-furniture-editor.content:menu.multimedia:content.${ SKU }`);
							if (!furnitureComponent) {
								continue;
							}
							if (!furnitureComponent.component_isDisabled) {
								await furnitureComponent.component_disable();
							}
						}
					} else {
						for (const SKU of [ '17PB01A', '17PB01B', '17PB01E', '17PB01D' ]) {
							const furnitureComponent = this.component_findMaybe(`@.modules:panel.viewer-planner-furniture-editor.content:menu.multimedia:content.${ SKU }`);
							if (!furnitureComponent) {
								continue;
							}
							if (furnitureComponent.component_isDisabled) {
								await furnitureComponent.component_enable();
							}
						}
					}
					if (this.hasThisModuleUsed('standing-objectsPositioningDynamicsSets')) {
						await (<StandingObjectsPositioningDynamicsSetsNS.Component> app.component_find(
							'@standing-objectsPositioningDynamicsSets')).refreshTvPositions();
					}
				} else if (path.startsWith('.personalization')) {
					const option = event.details.part.currentOption;
					if (!app.viewer.planner) {
						this.component_throwLibraryError('');
					}
					await app.viewer.setMaterialsGroups({
						values : option.metaData.viewerGroups
					});
				} else if (path.startsWith('.carpetImprint')) {
					const option = event.details.part.currentOption;
					if (!app.viewer.planner) {
						this.component_throwLibraryError('');
					}
					if (option){
						await this.viewer.setMaterialsGroups({
							values : option.metaData.viewerGroups
						});
					}

				} else if (path.startsWith('.wall')) {
					if (!this.isChangingProduct) {
						const option = event.details.part.currentOption;
						if (!app.viewer.planner) {
							this.component_throwLibraryError('');
						}
						if (option) {
							await this.viewer.setMaterialsGroups({
								values : option.metaData.viewerGroups
							});
						}
					}
				} else if (path.startsWith('.counter')) {
					// if (!this.isChangingProduct) {
						const option = event.details.part.currentOption;
						if (!app.viewer.planner) {
							this.component_throwLibraryError('');
						}
						if (option) {
							await this.viewer.setMaterialsGroups({
								values : option.metaData.viewerGroups
							});
						}
					// }
				} else {
					if (event.subType ===
						'partOptionUsed' &&
						event.details.part.reference &&
						(event.details.part.reference.value === 'furniture-plant-siae')
					)
					{
						if (!this.viewer.planner) {
							throw new Error;
						}
						const product = this.viewer.categories.map(x => x.products).
						flat().
						find(x => x.SKU === 'furniture-plant-siae');
						if (!product) {
							throw new Error;
						}
						const mod = this.modules.used.find(x => x.id === 'viewer-planner-furniture-editor');
						if (mod && mod.component) {
							const c = mod.component.component_find('@:menu').
							component_outerChilds.
							map(x => x.component_find('@:content')).
							map(x => x.component_outerChilds.find(x => x.id === 'furniture-plant-siae')).
							filter(x => x)[ 0 ];
							if (c) {
								(<CM.DOMComponent<any, any, any>> c).component_DOM.classList.add('limitReached');
								(<CM.DOMComponent<any, any, any>> c).component_DOM.title =
									app.currentLanguage === 'fr' ? 'Limité à un seul exemplaire' : 'Limited to one copy';
							}
						}
					}
					if (event.subType ===
						'partRemoved' &&
						event.details.part.reference &&
						event.details.part.reference.value ===
						'furniture-plant-siae')
					{
						if (!this.viewer.planner) {
							throw new Error;
						}
						const product = this.viewer.categories.map(x => x.products).
						flat().
						find(x => x.SKU === 'furniture-plant-siae');
						if (!product) {
							throw new Error;
						}
						if (!product.instances.size) {
							const mod = this.modules.used.find(x => x.id === 'viewer-planner-furniture-editor');
							if (mod && mod.component) {
								const c = mod.component.component_find('@:menu').
								component_outerChilds.
								map(x => x.component_find('@:content')).
								map(x => x.component_outerChilds.find(x => x.id === 'furniture-plant-siae')).
								filter(x => x)[ 0 ];
								if (c) {
									(<CM.DOMComponent<any, any, any>> c).component_DOM.classList.remove('limitReached');
									(<CM.DOMComponent<any, any, any>> c).component_DOM.title = '';
								}
							}
						}
					}
				}
			} else {
			}
			await this.viewer.batch(batch);
			await this.evaluateModulesConditions();
			const CP = this.currentProduct;
			if (this.basket && CP && CP.reference) {
				this.basket.basket.isReadyToComputePrice = true;
				if (CP.reference.value === 'siae') {
					const dimensionsPart = CP.parts.find(x => x.id === 'dimensions');
					const floorPart = CP.parts.find(x => x.id === 'floor');
					const wallPart = CP.parts.find(x => x.id === 'wall');
					const carpetImprintPart = CP.parts.find(x => x.id === 'personalization');
					if (!dimensionsPart || !floorPart || !carpetImprintPart || !wallPart) {
						this.component_throwLibraryError('');
					}
					if (!floorPart.currentOption || !carpetImprintPart.currentOption) {
						return;
					}
					const width = parseFloat(dimensionsPart.getPart([ 'width' ])?.reference?.value || '0');
					const depth = parseFloat(dimensionsPart.getPart([ 'depth' ])?.reference?.value || '0');
					const volume = width * depth;




					await floorPart.currentOption.updateVolume(volume);
					await carpetImprintPart.currentOption.updateVolume(1);






					const wallsEditor = <PlannerWallsEditorNS.Component> this.modules.used.find(x => x.id ===
						'viewer-planner-walls-editor')?.component;
					if (!wallsEditor) {
						this.component_throwLibraryError('');
					}
					if (wallPart.currentOption) {
						const walls = wallsEditor.walls.filter(x => x.viewerNode);
						const floorHeight = app.getFloorThickness();
						const wallsSurface = walls.reduce((a, w) => {
							const s = w.getVisibleWallSize();
							return a + (s.width * (s.height + floorHeight));
						}, 0);
						await wallPart.currentOption.updateVolume(wallsSurface);
					}
				} else if (CP.reference.value === 'business') {
					const dimensionsPart = CP.parts.find(x => x.id === 'dimensions');
					const floorPart = CP.parts.find(x => x.id === 'floor');
					const wallPart = CP.parts.find(x => x.id === 'wall');
					const carpetImprintPart = CP.parts.find(x => x.id === 'carpetImprint');
					if (!dimensionsPart || !floorPart || !carpetImprintPart || !wallPart) {
						this.component_throwLibraryError('');
					}
					if (!floorPart.currentOption || !carpetImprintPart.currentOption) {
						return;
					}
					const width = parseFloat(dimensionsPart.getPart([ 'width' ])?.reference?.value || '0');
					const depth = parseFloat(dimensionsPart.getPart([ 'depth' ])?.reference?.value || '0');
					let volume = 0;
					let carpetImprintVolume = 0;
					const layout = CP.getPart(['layout']);
					if(!layout){
						return;
					}
					const layoutOption = layout.currentOption;
					if(!layoutOption){
						return;
					}
					if(width != 0 || depth != 0){
						if(layoutOption.id === 'shape-back'){
							volume = (width-1) * (depth-0.5);
							carpetImprintVolume = 0.5*width+depth-0.5;
						}else if(layoutOption.id === 'shape-left-back-right'){
							volume = width * (depth-0.5);
							carpetImprintVolume = width*0.5;
						}else if(layoutOption.id === 'shape-no-wall'){
							volume = (width-1) * (depth-1);
							carpetImprintVolume = width + depth - 1;
						}else{
							volume = (width-0.5) * (depth-0.5);
							carpetImprintVolume = (width + depth - 0.5) / 2;
						}
					}
					await floorPart.currentOption.updateVolume(volume);
					await carpetImprintPart.currentOption.updateVolume(carpetImprintVolume);

					const wallsEditor = <PlannerWallsEditorNS.Component> this.modules.used.find(x => x.id ===
						'viewer-planner-walls-editor')?.component;
					if (!wallsEditor) {
						this.component_throwLibraryError('');
					}
					if (wallPart.currentOption) {
						const walls = wallsEditor.walls.filter(x => x.viewerNode);
						const floorHeight = app.getFloorThickness();
						const wallsSurface = walls.reduce((a, w) => {
							const s = w.getVisibleWallSize();
							return a + (s.width * (s.height + floorHeight));
						}, 0);
						await wallPart.currentOption.updateVolume(wallsSurface);
					}
				}  else if (CP.reference.value === 'confort' || CP.reference.value === 'prestige') {
					const dimensionsPart = CP.parts.find(x => x.id === 'dimensions');
					const floorPart = CP.parts.find(x => x.id === 'floor');
					const wallPart = CP.parts.find(x => x.id === 'wall');
					const carpetImprintPart = CP.parts.find(x => x.id === 'personalization');
					if (!dimensionsPart || !floorPart || !carpetImprintPart || !wallPart) {
						this.component_throwLibraryError('');
					}
					if (!floorPart.currentOption || !carpetImprintPart.currentOption) {
						return;
					}
					const width = parseFloat(dimensionsPart.getPart([ 'width' ])?.reference?.value || '0');
					const depth = parseFloat(dimensionsPart.getPart([ 'depth' ])?.reference?.value || '0');
					let volume = 0;
					let carpetImprintVolume = 0;
					const layout = CP.getPart(['layout']);
					if(!layout){
						return;
					}
					const layoutOption = layout.currentOption;
					if(!layoutOption){
						return;
					}
					if(width != 0 && depth != 0){
						if(layoutOption.id === 'shape-back'){
							volume = (width-1) * (depth-0.5);
							carpetImprintVolume = 0.5*width+depth-0.5;
						}else if(layoutOption.id === 'shape-left-back-right'){
							volume = width * (depth-0.5);
							carpetImprintVolume = width*0.5;
						}else if(layoutOption.id === 'shape-no-wall'){
							volume = (width-1) * (depth-1);
							carpetImprintVolume = width + depth - 1;
						}else{
							volume = (width-0.5) * (depth-0.5);
							carpetImprintVolume = (width + depth - 0.5) / 2;
						}
					}
					await floorPart.currentOption.updateVolume(volume);
					await carpetImprintPart.currentOption.updateVolume(carpetImprintVolume);

					const wallsEditor = <PlannerWallsEditorNS.Component> this.modules.used.find(x => x.id ===
						'viewer-planner-walls-editor')?.component;
					if (!wallsEditor) {
						this.component_throwLibraryError('');
					}
					if (wallPart.currentOption) {
						const walls = wallsEditor.walls.filter(x => x.viewerNode);
						const floorHeight = app.getFloorThickness();
						const wallsSurface = walls.reduce((a, w) => {
							const s = w.getVisibleWallSize();
							return a + (s.width * (s.height + floorHeight));
						}, 0);
						await wallPart.currentOption.updateVolume(wallsSurface);
					}
				}
			}
		});
		if (this.hasThisModuleUsed('viewer-planner-walls-editor')) {
			const walls = (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content'));
			for (const wall of walls.walls) {
				await wall.updateCustomizableAreaInfo();
			}
		}
		if (this.basket) {
			await this.basket.basket.computePrice();
			(<Text.Component> this.component_find('@visualization.basket.price.currency')).update(this.currentCurrency.id);
			(<Text.Component> this.component_find('@visualization.basket.price.symbol')).update(this.currentCurrency.symbol);
			(<Text.Component> this.component_find('@visualization.basket.price.price')).update(this.basket.basket.totalPrice.exclTax.toNumber().
			toString(10));
		}
	};

	getModelById(modelId : string) {
		void modelId;
	};

	async applyImageTransform(
		zone : {
			width : number;
			height : number;
			mesh : string;
			nodeLocalId : number;
		},
		image : {
			resolutionX : number;
			resolutionY : number;
			left : number;
			up : number;
			scale : number;
			rotation : number;
		}
	)
	{
		let size = 0;
		let baseOffsetX = 0;
		let baseOffsetY = 0;
		const kImg = image.resolutionX / image.resolutionY;
		const kWall = zone.width / zone.height;
		let kMin;
		if (kImg > kWall) {
			size = zone.width * image.scale;
			kMin = image.resolutionY;
			baseOffsetX = size / 2;
			baseOffsetY = (size * ((image.resolutionY / image.resolutionX))) / 2;
		} else {
			size = zone.height * image.scale;
			kMin = image.resolutionX;
			baseOffsetX = (size * ((image.resolutionX / image.resolutionY))) / 2;
			baseOffsetY = size / 2;
		}
		void baseOffsetX; //used to align left or right (center used currently)
		void baseOffsetY; //used to align top  or bottom(center used currently)
		const transform = {
			translation :    [
												 (0.5 - (zone.width / 2)) - image.left,
												 (0.5 - (zone.height / 2)) - image.up
											 ] as [ number, number ],
			scaling :        [
												 (image.resolutionY / kMin) / size,
												 (image.resolutionX / kMin) / size
											 ] as [ number, number ],
			rotationCenter : [
												 (zone.width / 2) + image.left,
												 (zone.height / 2) + image.up
											 ] as [ number, number ],
			rotation :       image.rotation,
			scaleX :         zone.width,
			scaleY :         zone.height
		};
		if (!this.viewer.viewerWindow || (this.viewer.loadingDetails.progress !== 1)) {
			await this.viewer.awaitLoading();
		}
		await this.viewer.transformMeshInstancesTextures([
			{
				name :        zone.mesh,
				nodeLocalId : zone.nodeLocalId,
				transform
			}
		]);
	};

	getWDH(refresh : boolean = false) {
		refresh = true;
		if (refresh !== true) {
			return (this.wdh);
		}
		if (!this.currentProduct) {
			this.component_throwUserError('');
		}
		const p = this.currentProduct;
		if (!p) {
			this.component_throwLibraryError('');
		}
		const shapePart = this.currentProduct.getPart([ 'dimensions' ]);
		const shapeWidthPart = shapePart.getPart([ 'width' ]);
		const shapeDepthPart = shapePart.getPart([ 'depth' ]);
		const shapeHeightPart = shapePart.getPart([ 'height' ]);
		if (
			!shapeWidthPart.reference ||
			!shapeDepthPart.reference ||
			!shapeHeightPart.reference
		)
		{
			return ({
				width :  0,
				depth :  0,
				height : 0
			});
		}
		return ({
			width :  parseFloat(shapeWidthPart.reference.value),
			depth :  parseFloat(shapeDepthPart.reference.value),
			height : parseFloat(shapeHeightPart.reference.value)
		});
	};

	async onBasketPartReferenceUpdated(ev : STD.events.Event<any, CM.Component<any, any>>) {
		const part = <BasketNS.Part> ev.details.part;
		if (
			(
				(part.id !== 'width') &&
				(part.id !== 'depth') &&
				(part.id !== 'height')
			) || (part.parent.id !== 'shape') ||
			!(part.parent instanceof BasketNS.Part) ||
			(part.parent.parent !== ev.details.product)
		)
		{
			return;
		}
		const details = {
			old : this.wdh,
			new : this.getWDH(true)
		};
		//nothing updated
		if (
			(details.old.width === details.new.width) &&
			(details.old.depth === details.new.depth) &&
			(details.old.height === details.new.height)
		)
		{
			return;
		}
		this.wdh = details.new;
		await this.dispatchEvent('standDimensions-update', { details });
	};

	async onResize(ev : STD.events.Event<any, CM.Component<any, any>>) {
		const { newSize } = ev.details;
		const newRenderMode = (
			((newSize.x <= 500) || (newSize.y <= 500)) ? 'small' :
				((newSize.x <= 1400) && (newSize.y <= 1400)) ? 'medium' :
					'wide'
		);
		await (<any> ev.target).updateRenderMode(newRenderMode);
	};

	async smallRMOpenCloseMenu(isOpen : boolean) { //eslint-disable-line require-await
		if (isOpen) {
			this.component_DOM.classList.remove('smallRMMenuClosed');
		} else {
			this.component_DOM.classList.add('smallRMMenuClosed');
		}
	};

	updateGlobalPanelStateCSSStatus(ev : STD.events.Event<any, CM.Component<any, any>>) {
		if ((<any> ev.target).currentSection) {
			this.component_DOM.classList.add('panelOpened');
		} else {
			this.component_DOM.classList.remove('panelOpened');
		}
	};

	private readonly awaitedToBeReady = new Set([
		'viewer',
		'planner',
		'app'
	]);
	private wdh = {
		width :  0,
		depth :  0,
		height : 0
	};

	// private async onMeshHighlight(ev : STD.events.Event<any, CM.Component<any, any>>) {
	// 	const hoveredNodeLocalId : number[] = ev.details.nodeLocalIds;
	// 	let instance : TProductInstance | null = null;
	// 	for (const _instance of this.planner.planner.instances) {
	// 		if (_instance.localsIds.find(x => hoveredNodeLocalId.includes(x))) {
	// 			instance = _instance;
	// 			break;
	// 		}
	// 	}
	// 	await this.viewer.setHighlight({
	// 		nodeLocalIds : instance ? instance.localsIds : []
	// 	});
	// };

	private computeStandBoundingBox = () => {
		const { width : x, depth : z, height : y } = this.getWDH();
		const bb = {
			width :  0,
			height : 0,
			depth :  0,
			maxX :   0.5 * x,
			maxY :   y,
			maxZ :   0.5 * z,
			minX :   -0.5 * x,
			minY :   -0.005,
			minZ :   -0.5 * z
		};
		bb.width = bb.maxX - bb.minX;
		bb.height = bb.maxY - bb.minY;
		bb.depth = bb.maxZ - bb.minZ;
		return (bb);
	};

	private async onAppReady() {
		const ev = this.component_getEventWhichRepresentNoEvent();
		for (const c of this.component_outerChilds) {
			if (c.component_configuration[ '@disableAutoStart' ]) {
				await c.component_start(ev);
			}
		}
		// (<any>document.querySelector('iframe')).contentWindow.emViewer.STD.exec('', 'rdr.toggleBBoxes');
	};

	computePriceForComponentsWhichHasSomeFreeElement(
		basketProduct : BasketNS.Product,
		productComponent : Viewer.ProductInstance
	) : number {
		if (basketProduct.id === 'siae') {
			const mapping : Record<
				string,
				{
					readonly freeItemsCount : number;
					readonly price : number;
				}
			> = {
				'siae:furniture-seat-c' :         {
					freeItemsCount : 4,
					price :          72
				},
				'siae:furniture-table' :          {
					freeItemsCount : 1,
					price :          80
				},
				'siae:furniture-briefcase-siae' : {
					freeItemsCount : 1,
					price :          65
				},
				'siae:furniture-trash' :          {
					freeItemsCount : 1,
					price :          7
				}
			};
			// const freeItemCount = mapping[productComponent.product.SKU];
			const pricingForThisProductComponent = mapping[ `${ productComponent.name }:${ productComponent.product.SKU }` ];
			if (!pricingForThisProductComponent) {
				this.component_throwLibraryError('');
			}
			if (productComponent.order <= pricingForThisProductComponent.freeItemsCount) {
				return 0;
			}
			return pricingForThisProductComponent.price;
		}
		this.component_throwLibraryError('not implemented!');
	};

	async fillComponentOptions(
		fromMenuComponent : CM.DOMComponent<any, any, any>,
		basketPart : BasketNS.Part,
		inputSelect : InputSelect.Component,
		productComponentOptionsDescriptors : NS.Configuration['productsOptions'],
		optionComponentType : string,
		event : STD.events.Event<CM.Component<any, any>, CM.Component<any, any>>
	)
	{
		const optionsInfos = productComponentOptionsDescriptors.map(optionDescriptor => {
			const translations = optionDescriptor.translations[ '@name' ];
			if (!translations) {
				this.component_throwUserError('Missing trad @name for floor option : ' + optionDescriptor.SKU);
			}
			const currentTrad = translations[ this.currentLanguage ];
			if (!currentTrad) {
				this.component_throwUserError('Missing trad @name for floor option :' +
					optionDescriptor.SKU +
					' for language ' +
					this.currentLanguage);
			}
			const option = basketPart.options.find(x => x.id === optionDescriptor.SKU) || basketPart.createOption({
				id :            optionDescriptor.SKU,
				title :         currentTrad,
				reference :     { value : optionDescriptor.SKU },
				components :    [],
				metaData :      <any> {
					viewerGroups : optionDescriptor.viewerGroups,
					translations : translations,
					SKU :          optionDescriptor.additionalData?.SKU,
					RGB :          optionDescriptor.additionalData?.RGB,
					icon :         optionDescriptor.iconURL
				},
				pricingInfo :   optionDescriptor.pricingInfo ? {
					pricing :                 BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized> optionDescriptor.pricingInfo.pricing),
					volumeComputationMethod : optionDescriptor.pricingInfo.volumeComputationMethod
				} : null,
				technicalFees : null
			});
			if (!option) {
				this.component_throwLibraryError('');
			}
			return {
				descriptor : optionDescriptor,
				option
			};
		});
		const x2f = async (optionsInfo : typeof optionsInfos) => {
			for (const optionInfo of optionsInfo) {
				// const price = optionInfo.option.pricingInfo?.pricing.resolve() || null;
				const tr = STD.translationsManager.languages.reduce<Record<string, string>>((a, c) => {
					a[ c ] = optionInfo.descriptor.SKU;
					return a;
				}, {});
				type TConf = InputButton.Configuration & {
					readonly            SKU : string;
					readonly  component : string;
				};
				const tile = <InputButton.Component> await inputSelect.component_instantiateOuterChild<TConf>(
					{
						type :          optionComponentType,
						id :            optionInfo.option.id,
						tree :          this.component_tree,
						slot :          null,
						isSlot :        false,
						declaredSlot :  null,
						configuration : {
							SKU :                    optionInfo.descriptor.SKU,
							component :              optionInfo.descriptor.component,
							'@innerConfigurations' : {
								'image' : <Image.Configuration> {
									'@translations' : {
										'name' : tr
									},
									'URL' :           optionInfo.descriptor.iconURL
								}
							},
							'@translations' :        {
								text : STD.translationsManager.languages.reduce<STD.translationsManager.TranslationMapping>((a, c) => {
									a[ c ] =
										`${ (<STD.translationsManager.TranslationMapping> optionInfo.descriptor.translations[ '@name' ])[ c ] } ${ optionInfo.descriptor.additionalData ?
											'(' + optionInfo.descriptor.additionalData.SKU + ')' :
											'' }`;
									return a;
								}, {})
							}
						}
					}
				);
				await tile.component_initialize();
				await tile.component_start(event);
			}
		};
		const componentWhichHasTheTranslations = (<any> fromMenuComponent.component_configuration[ '@translations' ])?.optional ?
			fromMenuComponent :
			this;
		this.component_defineTranslation('included', (<any> this.component_configuration[ '@translations' ]).included, true);
		this.component_defineTranslation(
			'optional',
			(<any> componentWhichHasTheTranslations.component_configuration[ '@translations' ]).optional,
			true
		);
		const includedOptions = optionsInfos.filter(x => !x.option.pricingInfo ||
			x.option.pricingInfo.pricing.resolve().isNull());
		const inOptionOptions = optionsInfos.filter(x => !includedOptions.includes(x));
		inputSelect.component_DOM.appendChild(STD.DOM.factory({
			tag :       'h3',
			dataset :   {
				translation : <string> this.component_generateTranslationId('included')
			},
			classList : [ 'fake-title' ],
			style :     {
				marginRight : '100%'
			}
		}));
		await x2f(includedOptions);
		if(inOptionOptions.length != 0){
			const product = app.currentProduct;
			if(!product){
				this.component_throwUserError('');
			}
			if(product.id === 'business' && (<any>componentWhichHasTheTranslations.component_configuration[ '@translations' ]).optionalBusiness){
				inputSelect.component_DOM.appendChild(STD.DOM.factory({
					tag :       'h3',
					dataset :   {
						translation : <string> componentWhichHasTheTranslations.component_generateTranslationId('optionalBusiness')
					},
					classList : [ 'fake-title' ],
					style :     {
						marginRight : '100%',
						whiteSpace :  'nowrap'
					}
				}));
			}else{
				inputSelect.component_DOM.appendChild(STD.DOM.factory({
					tag :       'h3',
					dataset :   {
						translation : <string> componentWhichHasTheTranslations.component_generateTranslationId('optional')
					},
					classList : [ 'fake-title' ],
					style :     {
						marginRight : '100%',
						whiteSpace :  'nowrap'
					}
				}));
			}
			await x2f(inOptionOptions);
		}
	};

	deduceCurrencyPricingKey() {
		return this.currentCurrency.id;
	};

	deduceInOptionOrIncludedPricingKey(params : { host : BasketNS.Product | BasketNS.Part | BasketNS.Option | null }) {
		const { host } = params;
		if (!host) {
			return 'inOption';
		} else if (host instanceof BasketNS.Part) {
			const componentSKU = host.id.slice(0, host.id.lastIndexOf('-'));
			const allProducts = app.viewer.categories.map(x => x.products).flat();
			const mapping : Record<
				string,
				{
					readonly freeItemsCount : number;
				}
			> = {
				'furniture-seat-c' :         {
					freeItemsCount : 4
				},
				'furniture-table' :          {
					freeItemsCount : 1
				},
				'furniture-briefcase-siae' : {
					freeItemsCount : 1
				},
				'furniture-trash' :          {
					freeItemsCount : 1
				}
			};
			const data = mapping[ componentSKU ];
			const component = allProducts.find(x => x.SKU === componentSKU);
			if (!component || !data) {
				throw new Error;
			}
			const localId = (<any> host.metaData).nodeLocalId;
			const allProductInstances = Array.from(component.instances);
			const instance = allProductInstances.find(x => x.localId === localId);
			if (instance && instance.order <= data.freeItemsCount) {
				return 'included';
			}
			if ((allProductInstances.length + 1) <= data.freeItemsCount) {
				return 'included';
			}
			return 'inOption';
		} else {
		}
		throw new Error('');
	};

	standingHangingSignCustomConfiguratorPrepare() {
		return async (product : Viewer.ProductInstance) => {
			const customConfigurator = <CM.DOMComponent<any, any, any>> this.component_find(
				'K.customConfigurators.hangingSignCustomConfigurator');
			const select = <InputSelect.Component> customConfigurator.component_find('@option.options');
			if (!product.basketPart) {
				throw new Error;
			}
			for (const c of Array.from(select.component_outerChilds)) {
				await c.component_delete();
			}
			for (const fakeTitle of select.component_querySelectorAll('.fake-title')) {
				fakeTitle.remove();
			}
			await this.fillComponentOptions(
				customConfigurator,
				product.basketPart,
				select,
				[
					{
						component :      product.product.SKU,
						SKU :            'std',
						viewerGroups :   [],
						products :       [],
						iconURL :        (<any> window).serverParameters.app.assetsPath + 'Light-off.svg',
						translations :   {
							'@name' : {
								'en' : 'No light',
								'fr' : 'Pas de lumière'
							}
						},
						pricingInfo :    null,
						additionalData : null
					},
					{
						component :      product.product.SKU,
						SKU :            '14PB01C',
						viewerGroups :   [],
						products :       [],
						iconURL :        (<any> window).serverParameters.app.assetsPath + 'Light-on.svg',
						translations :   {
							'@name' : {
								'en' : 'Light Option',
								'fr' : 'Option lumineuse'
							}
						},
						pricingInfo :    {
							pricing :                 {
								type :  'final',
								price : {
									inclTax : 247,
									exclTax : 247,
									taxes :   []
								}
							},
							volumeComputationMethod : 'unit'
						},
						additionalData : {
							SKU : 'power supplied included'
						}
					}
				],
				'standing-hangingSignCustomConfiguratorTile',
				this.component_getEventWhichRepresentNoEvent()
			);

			if (!product.basketPart.currentOption) {
				throw new Error;
			}
			await select.updateValue(select.component_find('@.' + product.basketPart.currentOption.id));

			STD.translationsManager.update(customConfigurator.component_DOM);
		};
	};

	async standingHangingSignCustomConfiguratorGet() {
		return "K.customConfigurators.hangingSignCustomConfigurator";
	};

	async hangingSignCustomConfiguratorOnValue(event : STD.events.Event<any, any>) {

		if (!this.viewer.currentSelectedInstance || !this.viewer.currentSelectedInstance.basketPart) {
			return;
		}
		const p = this.viewer.currentSelectedInstance.basketPart;
		if (!p) {
			this.component_throwLibraryError('');
		}
		
		const optionSKU = event.details.newValue.id;
		if(p.currentOption?.id === optionSKU){
			return;
		}
		const option = p.options.find(x => x.id === optionSKU);
		if (!option) {
			this.component_throwLibraryError('');
		}
		await p.useOption(option.id);
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component> [ 0 ],
		configuration : TConfiguration
	)
	{
		Object.assign(configuration, {
			replayUserInputsSorter : (path : string, boundComponent : CM.Component<any, any>, currentOrder : number) => {
				void boundComponent;
				if (path === 'K.visualization.V') {
					return 0;
				} else if (path === 'K.modules:panel.product-editor.content') {
					return 1;
				} else if (path === 'K.modules:panel.viewer-planner-layout-editor.content') {
					return 2;
				}
				return currentOrder + 20;
			}
		});
		super(descriptor, configuration);
		if (!this.parameters.useLocalViewers &&
			!this.clientAppParameters.server.isOnProd &&
			this.clientAppParameters.app.isTestMode)
		{
			(<any> this.clientAppParameters.app).URLPrefix = this.clientAppParameters.app.URLPrefix.replace(
				this.clientAppParameters.app.code,
				this.component_configuration.productionAppCode
			);
		}
		for (const [ k, v ] of Object.entries(this.component_configuration.theme.foreground)) {
			document.documentElement.style.setProperty(`--mpaengine-fg-${ k }`, v);
		}
		for (const [ k, v ] of Object.entries(this.component_configuration.theme.background)) {
			document.documentElement.style.setProperty(`--mpaengine-bg-${ k }`, v);
		}
		document.head.appendChild(STD.DOM.factory({
			tag :     'style',
			dataset : {
				id : 'theme'
			},
			html :    Object.entries(this.component_configuration.theme.customCSS).
								map(([ selector, rule ]) => `${ selector } { ${ rule } }`).
								join('\n')
		}));
		/** Prevent unwanted dragging **/
		window.addEventListener('dragstart', (ev) => {
			ev.preventDefault();
		}, { capture : true, passive : false });
		document.body.addEventListener('keydown', this.onKeyDown.bind(this), { capture : true });
	};
};
export namespace NS {
	export const Component = MPAEngineApplicationComponent;
	export type Component = MPAEngineApplicationComponent<any, IMPAEngineApplicationComponentConfiguration>;
	export type Configuration = IMPAEngineApplicationComponentConfiguration;
}

CM.declare({
	id :   'mpaengine-application',
	type : MPAEngineApplicationComponent
});

import registerModuleBasket                       from '../../../modules/oms-basket';
import registerModulePlanner                      from '../../../modules/viewer-planner';
import registerModulePlannerLayoutEditor          from '../../../modules/viewer-planner-layout-editor';
import registerModulePlannerDimensionsEditor      from '../../../modules/viewer-planner-dimensionsEditor';
import registerModulePlannerFloorEditor           from '../../../modules/viewer-planner-floor-editor';
import registerModulePlannerWallsEditor           from '../../../modules/viewer-planner-walls-editor';
import registerModulePlannerFurniture             from '../../../modules/viewer-planner-furniture-editor';
import registerModuleProductChoose                from '../../../modules/product-editor';
import registerModuleCustomerInformationForm      from '../../../modules/oms-customerForm';
import registerModuleCustomerInformationFormModal from '../../../modules/oms-customerFormModal';
import registerModuleCommandConfirmationModal     from '../../../modules/oms-processConfirmationModal';
import registerModuleLanguageSelector             from '../../../modules/language-selector';
import registerModuleCustomerStanding_PartitionsHeads
																									from '../../../modules/customers/standing/standing-partitionsHeads';
import registerModuleCustomerStanding_PartitionsHeadsPrestige
																									from '../../../modules/customers/standing/standing-partitionsHeadsPrestige';
import registerModuleCustomerStanding_Plinth
																									from '../../../modules/customers/standing/standing-plinth';
import registerModuleCustomerStanding_PersonalizationEditor
																									from '../../../modules/customers/standing/standing-personalizationEditor';
import registerModuleCustomerStanding_modeCustomer
																									from '../../../modules/customers/standing/standing-modeCustomer';
import registerModuleCustomerStanding_carpetImprint
																									from '../../../modules/customers/standing/standing-carpetImprint';
import registerModuleCustomerStanding_counter
																									from '../../../modules/customers/standing/standing-counter';
import registerModuleCustomerStanding_storage
																									from '../../../modules/customers/standing/standing-storage';
import registerModuleCustomerStanding_carpetImprintEditor
																									from '../../../modules/customers/standing/standing-carpetImprintEditor';
import registerModuleCustomerStanding_Lighting
																									from '../../../modules/customers/standing/standing-lighting';
import registerModuleCustomerStanding_ObjectsPositioningDynamicsSets
																									from '../../../modules/customers/standing/standing-objectsPositioningDynamicsSets';
import registerModuleCustomerStanding_arch
																									from '../../../modules/customers/standing/standing-arch';
import registerModulePlannerMenu                  from '../../../modules/viewer-menu';
import registerModuleCustomerStanding_highSign
																									from '../../../modules/customers/standing/standing-highSign';
import registerModuleCustomerStanding_TV          from '../../../modules/customers/standing/standing-tv';
import registerModuleCustomerStanding_Banner
																									from '../../../modules/customers/standing/standing-banner';
import registerModuleCustomerStanding_Furniture
																									from '../../../modules/customers/standing/standing-furniture';
import registerModuleCustomerStanding_Questions
																									from '../../../modules/customers/standing/standing-questions';
import registerModuleCustomerStanding_pdfAccessories

																									from '../../../modules/customers/standing/standing-pdfAccessories';
import registerModuleCustomerStanding_hangingSignCustomConfigurator
																									from '../../../modules/customers/standing/standing-hangingSignCustomConfigurator';
import registerModuleCustomerStanding             from '../../../modules/customers/standing/standing';
import EngineModule                               from '../../../EngineModule';
import { NS as ApplicationNS }                    from '../../Application/Application';
import { NS as OMSBasketNS }                      from '../../oms/Basket/OMSBasket';
import Viewer, { NS as ViewerNS }                 from '../../viewer/Viewer/Viewer';
import { NS as ViewerGraphicsNS }                 from '../../viewer/Graphics/ViewerGraphics';
import {
	NS as PlannerLayoutEditorNS
}                                                 from '../../viewer/planner/layout/Editor/PlannerLayoutEditor';
import {
	NS as PlannerDimensionsEditorNS
}                                                 from '../../viewer/planner/DimensionsEditor/PlannerDimensionsEditor';
import {
	NS as PlannerWallsEditorNS
}                                                 from '../../viewer/planner/walls/Editor/PlannerWallsEditor';
import {
	NS as PlannerFloorEditorNS
}                                                 from '../../viewer/planner/floor/Editor/PlannerFloorEditor';
import {
	NS as PlannerFurnitureEditorNS
}                                                 from '../../viewer/planner/furniture/Editor/PlannerFurnitureEditor';
import {
	NS as OMSCustomerFormButtonNS
}                                                 from '../../oms/CustomerFormButton/OMSCustomerFormButton';
// import { NS as OMSCustomerFormNS }                    from '../../oms/CustomerForm/OMSCustomerForm';
import { NS as OMSCustomerModalNS }               from '../../oms/CustomerFormModal/OMSCustomerFormModal';
import {
	NS as ProductReplayLinkModalNS
}                                                 from '../../product/ReplayLinkModal/ProductReplayLinkModal';
import {
	NS as StandingPartitionsHeadsNS
}                                                 from '../../customers/standing/StandingPartitionsHeads/StandingPartitionsHeads';
import {
	NS as StandingPartitionsHeadsPrestigeNS
}                                                 from '../../customers/standing/StandingPartitionsHeadsPrestige/StandingPartitionsHeadsPrestige';
import {
	NS as StandingPlinthNS
}                                                 from '../../customers/standing/StandingPlinth/StandingPlinth';
import {
	NS as StandingCounterNS
}                                                 from '../../customers/standing/StandingCounter/StandingCounter';
import {
	NS as StandingBannerConfortNS
}                                                 from '../../customers/standing/StandingBannerConfort/StandingBannerConfort';
import {
	NS as StandingCarpetImprintNS
}                                                 from '../../customers/standing/StandingCarpetImprint/StandingCarpetImprint';
import {
	NS as StandingPersonalizationEditorNS
}                                                 from '../../customers/standing/StandingPersonalizationEditor/StandingPersonalizationEditor';
import {
	NS as StandingCarpetImprintEditorNS
}                                                 from '../../customers/standing/StandingCarpetImprintEditor/StandingCarpetImprintEditor';
import {
	NS as StandingArchNS
}                                                 from '../../customers/standing/StandingArch/StandingArch';
import {
	NS as StandingStorageNS
}                                                 from '../../customers/standing/StandingStorage/StandingStorage';
import {
	NS as StandingCustomerModeNS
}                                                 from '../../customers/standing/StandingCustomerMode/StandingCustomerMode';
import {
	NS as StandingLightingNS
}                                                 from '../../customers/standing/StandingLighting/StandingLighting';
import {
	NS as StandingHighSignNS
}                                                 from '../../customers/standing/StandingHighSign/StandingHighSign';
import { NS as StandingTVNS }                     from '../../customers/standing/StandingTV/StandingTV';
import { NS as StandingNumericOptionsTileNS }     from '../../customers/standing/StandingNumericOptionsTile/StandingNumericOptionsTile';
import { NS as StandingPDFAccessoriesNS }                     from '../../customers/standing/StandingPDFAccessories/StandingPDFAccessories';
import {
	NS as StandingBannerNS
}                                                 from '../../customers/standing/StandingBanner/StandingBanner';
import {
	NS as StandingFurnitureNS
}                                                 from '../../customers/standing/StandingFurniture/StandingFurniture';
import {
	NS as StandingObjectsPositioningDynamicsSetsNS
}                                                 from '../../customers/standing/StandingObjectsPositioningDynamicsSets/StandingObjectsPositioningDynamicsSets';import { OMSCustomerFormComponent } from '../../oms/CustomerForm/OMSCustomerForm';

